// ===== BEGIN FILE: src/components/chat/ChatOutputPanel.tsx =====
/**
 * ChatOutputPanel — Column 3 of the "Ask Shreem" 3-column desktop layout.
 *
 * Panels:
 *  'welcome'         — Greeting + personal info + chart essentials + 3 tiles
 *  'current_period'  — MD/AD/PD/SD dasha table
 *  'house_dashboard' — 12 house scores grid
 *  'swot'            — House + dasha SWOT overview
 *  'life_area'       — Life area focus: personality + filtered house cards + area score
 *  'life_remedies'    — Important life area remedies (LifeAreaRemediesTable filtered to selected area)
 */

import React, { useRef, useEffect, useState } from 'react';
import { Box, Typography, Chip, Card, Divider, LinearProgress, Button, Tooltip } from '@mui/joy';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TimelineIcon from '@mui/icons-material/Timeline';
import GridViewIcon from '@mui/icons-material/GridView';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import type { ChatSourceData } from '../../utils/chatContext';
import LifeAreaRemediesTable from '../LifeAreaRemediesTable';
import type { DashaContext } from '../../astro/feeds/types';
import { LIFE_AREAS, type LifeAreaKey } from '../../utils/lifeAreas';
import { computeHouseCombinedScore, computeDashaImpactByHouse, computeTransitImpactByHouse } from '../../utils/houseTemporalScores';
import { computeAllHouseLFIs, getFrictionLevel, FRICTION_LEVEL_COLORS } from '../../astro/karmic/karmicFrictionEngine';

export type ChatOutputType =
  | 'welcome'
  | 'current_period'
  | 'house_dashboard'
  | 'swot'
  | 'life_area'
  | 'life_remedies';

interface ChatOutputPanelProps {
  outputType: ChatOutputType;
  /** Life area selected in column 1 — drives 'life_area' panel */
  selectedLifeArea?: LifeAreaKey | null;
  /** Full chart context — null when no birth data yet */
  contextData: ChatSourceData | null;
  /** Called when "Go to Dashboard" is clicked */
  onGoToDashboard: () => void;
  /** Called when user clicks a tile to switch output type */
  onOutputTypeChange?: (type: ChatOutputType) => void;
  /** Called when user navigates to a life area (e.g. Next button) */
  onLifeAreaChange?: (area: LifeAreaKey) => void;
  /** User display name for personalised greeting */
  userName?: string;
}

// --- Sign Lord map (1-indexed signs) ---
const SIGN_LORD_MAP: Record<number, string> = {
  1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun',
  6: 'Mercury', 7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn',
  11: 'Saturn', 12: 'Jupiter'
};

// --- House keyword map ---
const HOUSE_KEYWORDS: Record<number, string> = {
  1: 'Self', 2: 'Wealth', 3: 'Courage', 4: 'Home',
  5: 'Intellect', 6: 'Health', 7: 'Partnership', 8: 'Change',
  9: 'Fortune', 10: 'Career', 11: 'Gains', 12: 'Liberation',
};

// --- Ascendant personality descriptions (1-indexed signs) ---
const ASCENDANT_PERSONALITY: Record<string, { title: string; desc: string }> = {
  'Aries':       { title: 'Dynamic & Pioneering', desc: 'Bold, competitive, and driven by a strong will to lead. Natural courage and physical vitality define this personality. Quick to initiate and willing to take risks others avoid.' },
  'Taurus':      { title: 'Stable & Practical', desc: 'Grounded, comfort-loving, and calm under pressure. Strong physical presence with great endurance. Highly reliable with a deep appreciation for beauty and material security.' },
  'Gemini':      { title: 'Quick-Witted & Adaptable', desc: 'Intellectually agile, curious, and communicative. Thrives on variety and can see multiple perspectives simultaneously. A natural networker with versatile skills.' },
  'Cancer':      { title: 'Nurturing & Intuitive', desc: 'Deeply sensitive, home-loving, and fiercely protective of loved ones. Strong emotional intelligence and empathy. Inner world is rich and mood can shift like tides.' },
  'Leo':         { title: 'Regal & Confident', desc: 'Generous, dramatic, and naturally commanding. Radiates warmth and creative energy. Leadership comes naturally, along with a desire to be appreciated and respected.' },
  'Virgo':       { title: 'Analytical & Service-Oriented', desc: 'Detail-focused, practical, and conscientious. Strong discernment with a deep desire to be of use. Health and daily routines are important pillars of life.' },
  'Libra':       { title: 'Balanced & Diplomatic', desc: 'Values fairness, beauty, and harmony in all areas. Gifted at partnership and social settings. Seeks balance but can be indecisive when pulled between equally valid options.' },
  'Scorpio':     { title: 'Intense & Transformative', desc: 'Deep perception, strong willpower, and remarkable resilience. Investigative by nature with an ability to regenerate after crisis. Magnetism and intensity are hallmarks.' },
  'Sagittarius': { title: 'Philosophical & Adventurous', desc: 'Freedom-loving, spiritually inclined, and broadly optimistic. Seeks meaning and truth through experience, travel, and higher learning. Generous and inspirational.' },
  'Capricorn':   { title: 'Disciplined & Ambitious', desc: 'Patient, methodical, and strategic. Long-term thinking and strong work ethic define this personality. Success comes steadily through persistence and responsibility.' },
  'Aquarius':    { title: 'Innovative & Humanitarian', desc: 'Independent, intellectual, and drawn to progressive ideas. Visionary with strong idealistic principles. Values freedom of thought and unconventional approaches.' },
  'Pisces':      { title: 'Intuitive & Compassionate', desc: 'Empathetic, spiritual, and creatively gifted. Sensitive to subtle energies with a deep inner life. Boundaries between self and others can blur — both a gift and a challenge.' },
};

// --- Moon nakshatra nature traits (strip pada info before lookup) ---
const NAKSHATRA_NATURE: Record<string, string> = {
  'Ashwini':            'Quick, Healing, Pioneering, Impulsive, Energetic',
  'Bharani':            'Determined, Passionate, Disciplined, Intense, Responsible',
  'Krittika':           'Sharp, Ambitious, Purifying, Direct, Resolute',
  'Rohini':             'Charismatic, Sensual, Creative, Stable, Magnetic',
  'Mrigashira':         'Curious, Gentle, Searching, Refined, Sensitive',
  'Ardra':              'Transformative, Intense, Sharp, Emotional, Stormy',
  'Punarvasu':          'Generous, Nurturing, Philosophical, Optimistic, Restoring',
  'Pushya':             'Nourishing, Responsible, Devout, Conservative, Caring',
  'Ashlesha':           'Perceptive, Secretive, Healing, Intense, Serpentine',
  'Magha':              'Regal, Ancestral, Dignified, Authoritative, Traditional',
  'Purva Phalguni':     'Playful, Romantic, Generous, Relaxed, Creative',
  'Uttara Phalguni':    'Dependable, Helpful, Practical, Sociable, Fortunate in Marriage',
  'Hasta':              'Skillful, Clever, Persuasive, Neat, Crafty',
  'Chitra':             'Artistic, Perfectionist, Stylish, Balanced, Creative',
  'Swati':              'Independent, Restless, Diplomatic, Flexible, Wind-like',
  'Vishakha':           'Determined, Focused, Purposeful, Intense, Ambitious',
  'Anuradha':           'Devoted, Friendly, Cooperative, Disciplined, Loyal',
  'Jyeshtha':           'Senior, Protective, Resourceful, Authoritative, Proud',
  'Mula':               'Investigative, Philosophical, Root-seeking, Intense, Uncompromising',
  'Purva Ashadha':      'Invincible, Proud, Philosophical, Energetic, Early Victory',
  'Uttara Ashadha':     'Long Life, Patient, Socially Responsible, Rigid, Sincere',
  'Shravana':           'Learned, Listening, Communicative, Traditional, Steady',
  'Dhanishtha':         'Wealthy, Musical, Courageous, Charitable, Active',
  'Shatabhisha':        'Healing, Independent, Secretive, Scientific, Reclusive',
  'Purva Bhadrapada':   'Intense, Fiery, Dual-natured, Spiritually Evolving, Restless',
  'Uttara Bhadrapada':  'Wise, Compassionate, Deep, Patient, Serpentine Energy',
  'Revati':             'Nurturing, Compassionate, Spiritual, Wealthy, Journey-oriented',
};

// ---
// Shared helper — enrich contextData with fallback dasha/transit impact maps
// if the pre-computed ones are missing (e.g. chatContextData was evaluated before
// functionalNatureData / transitData were ready)
// ---
function enrichContextData(ctx: ChatSourceData): ChatSourceData {
  let dashaImpactByHouse = ctx.dashaImpactByHouse;
  let transitImpactByHouse = ctx.transitImpactByHouse;

  // Fallback: compute dasha impact from raw dashaContext + functionalNature
  if ((!dashaImpactByHouse || Object.keys(dashaImpactByHouse).length === 0) &&
      ctx.dashaContext && ctx.functionalNature) {
    dashaImpactByHouse = computeDashaImpactByHouse(
      ctx.dashaContext as any,
      ctx.functionalNature as Record<string, string>,
    );
  }

  // Fallback: compute transit impact from raw transitData + functionalNature
  if ((!transitImpactByHouse || Object.keys(transitImpactByHouse).length === 0) &&
      ctx.transitData?.placements && ctx.functionalNature) {
    const positions = Object.entries(ctx.transitData.placements)
      .filter(([, p]: [string, any]) => p?.house)
      .map(([planet, p]: [string, any]) => ({ planet, house: p.house as number }));
    transitImpactByHouse = computeTransitImpactByHouse(
      positions,
      ctx.functionalNature as Record<string, string>,
      ctx.blendedPlanetPct ?? undefined,
      ctx.transitData?.dignities as Partial<Record<string, string>> | undefined,
      null, null, null,
    );
  }

  if (dashaImpactByHouse === ctx.dashaImpactByHouse &&
      transitImpactByHouse === ctx.transitImpactByHouse) {
    return ctx; // nothing changed — return same reference (no re-render)
  }
  return { ...ctx, dashaImpactByHouse, transitImpactByHouse };
}

// ---
// Shared helper — compute per-house LFI from ChatSourceData
// Returns null per house if frictionScoresByPlanet is not available
// ---
function _buildHouseLFIs(contextData: ChatSourceData): Record<number, number> | null {
  const friction = contextData.frictionScoresByPlanet;
  if (!friction || Object.keys(friction).length === 0) return null;
  const placements = contextData.astroData?.placements as Record<string, { house: number }> | undefined;
  const ascIdx = contextData.astroData?.asc?.ascendantSignIndex;
  if (!placements || !ascIdx) return null;
  const all = computeAllHouseLFIs(placements, ascIdx, friction);
  return Object.fromEntries(Object.entries(all).map(([h, r]) => [Number(h), r.lfi]));
}

// ---
// InsightsWelcomePanel — greeting + personal info + chart essentials + tiles
// ---

/**
 * Staged sequential reveal:
 *  Stage 0  — nothing (mount)
 *  Stage 1  — greeting
 *  Stage 2  — "Analyzing..." loading bar
 *  Stage 3  — Personal Information header
 *  Stage 4-7 — personal info rows 1-4
 *  Stage 8  — Chart Essentials header
 *  Stage 9-11 — chart rows 1-3
 *  Stage 12 — Quick Insights tiles
 *
 * Timing: ~5.9 s total. Auto-scroll after each stage.
 */
const STAGE_TIMINGS = [
  0,    // 0 → nothing
  100,  // 0 → 1: greeting
  800,  // 1 → 2: loading bar
  2000, // 2 → 3: weekly briefing + PI header
  2500, // 3 → 4: PI row 1
  2950, // 4 → 5: PI row 2
  3380, // 5 → 6: PI row 3
  3780, // 6 → 7: PI row 4
  4200, // 7 → 8: CE header
  4650, // 8 → 9: CE row 1
  5050, // 9 → 10: CE row 2
  5400, // 10 → 11: CE row 3
  5900, // 11 → 12: tiles
];

// ---
// computeWeeklyBriefing — derives crisp This-Week guidance from contextData
// No GPT required: combustion + retrograde + feeds + transit dignity + SD dasha nature
// ---
function computeWeeklyBriefing(ctx: ChatSourceData): {
  favorable: string[];
  caution: string[];
  avoid: string[];
} {
  const favorable: string[] = [];
  const caution: string[] = [];
  const avoid: string[] = [];

  // 1. Combust planet tips (transit planets near Sun)
  const COMBUST_TIPS: Partial<Record<string, string>> = {
    Mercury: 'Mercury combust — re-read emails & messages carefully before sending',
    Venus:   'Venus combust — avoid big purchases or relationship decisions',
    Mars:    'Mars combust — channel energy carefully, avoid impulsive moves',
    Jupiter: 'Jupiter combust — double-check guidance received, advice may be skewed',
    Saturn:  'Saturn combust — delays likely, extra patience required',
    Moon:    'Moon combust — emotions feel clouded, allow extra time for decisions',
  };
  for (const [p, v] of Object.entries(ctx.combust ?? {})) {
    if ((v as any)?.combust) {
      const tip = COMBUST_TIPS[p];
      if (tip) avoid.push(tip);
    }
  }

  // 2. Transit retrograde tips (slow planets only)
  const RETRO_TIPS: Partial<Record<string, string>> = {
    Mercury: 'Mercury retrograde — review plans, avoid launches or signing contracts',
    Mars:    'Mars retrograde — complete old work, skip new major initiatives',
    Jupiter: 'Jupiter retrograde — reflect on beliefs; expansion is temporarily delayed',
    Saturn:  'Saturn retrograde — past karma resurfaces, review long-term commitments',
    Venus:   'Venus retrograde — pause new relationships and luxury spending',
  };
  const transRetro = (ctx as any).transitData?.retrogrades as Partial<Record<string, boolean>> | undefined;
  for (const [p, isRetro] of Object.entries(transRetro ?? {})) {
    if (isRetro && !['Rahu', 'Ketu', 'Sun', 'Moon'].includes(p)) {
      const tip = RETRO_TIPS[p];
      if (tip) caution.push(tip);
    }
  }

  // 3. Jupiter & Saturn transit dignity
  const digs = ctx.transitData?.dignities as Partial<Record<string, string>> | undefined;
  if (digs) {
    if (digs['Jupiter'] === 'exalted' || digs['Jupiter'] === 'moolatrikona') {
      favorable.unshift('Jupiter is powerful — excellent for learning, growth & guidance');
    } else if (digs['Jupiter'] === 'debilitated') {
      caution.push('Jupiter weakened — optimism may be misplaced, verify plans carefully');
    }
    if (digs['Saturn'] === 'exalted' || digs['Saturn'] === 'moolatrikona') {
      favorable.push('Saturn is strong — disciplined effort yields steady results');
    } else if (digs['Saturn'] === 'debilitated') {
      caution.push('Saturn debilitated — authority challenges, unexpected delays possible');
    }
  }

  // 4. Feed-based highlights: top active feeds in the next 7-day window
  const AREA_LABELS: Partial<Record<string, string>> = {
    Career: 'Career', Wealth: 'Wealth', Health: 'Health', Spouse: 'Relationships',
    Children: 'Children', Property: 'Home & Property', Investments: 'Investments',
    Travel: 'Travel', Self: 'Self', Mother: 'Family', Father: 'Family',
    AccidentRisk: 'Safety', Litigation: 'Legal matters', Spirituality: 'Spirituality',
  };
  const _now = new Date();
  const _weekEnd = new Date(_now); _weekEnd.setDate(_now.getDate() + 7);
  const weekFeeds = (ctx.feeds ?? []).filter((f: any) => {
    const win = f.window;
    if (!win) return false;
    try {
      const from = new Date(win.from ?? win.to ?? win.start);
      const to = new Date(win.to ?? win.from ?? win.end);
      return from <= _weekEnd && to >= _now;
    } catch { return false; }
  });
  const posFeeds = [...weekFeeds]
    .filter((f: any) => (f.score ?? 0) >= 35)
    .sort((a: any, b: any) => (b.score ?? 0) - (a.score ?? 0))
    .slice(0, 2);
  for (const f of posFeeds) {
    const sm = (f as any).shortMessage as string | undefined;
    if (sm && sm.length > 10) {
      // Strip ALL_CAPS cause prefix, e.g. "CAREER STRENGTH: Good energy..." → "Career: Good energy..."
      const withoutPrefix = sm.replace(/^[A-Z0-9][A-Z0-9 _&\-]+:\s*/, '');
      const areaLabel = AREA_LABELS[(f as any).lifeArea ?? ''] ?? (f as any).lifeArea ?? '';
      const line = areaLabel ? `${areaLabel}: ${withoutPrefix}` : withoutPrefix;
      favorable.push(line);
    } else {
      const area = AREA_LABELS[(f as any).lifeArea ?? ''] ?? (f as any).lifeArea ?? '';
      if (area) favorable.push(`${area}: favorable period — good time to act on opportunities`);
    }
  }
  const negFeeds = [...weekFeeds]
    .filter((f: any) => (f.score ?? 0) <= -30)
    .sort((a: any, b: any) => (a.score ?? 0) - (b.score ?? 0))
    .slice(0, 2);
  for (const f of negFeeds) {
    const sm = (f as any).shortMessage as string | undefined;
    if (sm && sm.length > 10) {
      const withoutPrefix = sm.replace(/^[A-Z0-9][A-Z0-9 _&\-]+:\s*/, '');
      const areaLabel = AREA_LABELS[(f as any).lifeArea ?? ''] ?? (f as any).lifeArea ?? '';
      const line = areaLabel ? `${areaLabel}: ${withoutPrefix}` : withoutPrefix;
      caution.push(line);
    } else {
      const area = AREA_LABELS[(f as any).lifeArea ?? ''] ?? (f as any).lifeArea ?? '';
      if (area) caution.push(`${area}: needs careful handling this week`);
    }
  }

  // 5. SD dasha lord functional nature (theme of current sub-sub-period)
  const fn = ctx.functionalNature as Partial<Record<string, string>> | undefined;
  const levels = (ctx.dashaContext as any)?.levels as Array<{ level: string; planet: string }> | undefined;
  const sdPlanet = levels?.find((l: any) => l.level === 'SD')?.planet
    ?? (ctx.dashaContext as any)?.SD?.planet;
  const sdNature = sdPlanet && fn ? fn[sdPlanet] : null;
  if (sdPlanet && sdNature === 'benefic' && favorable.length < 3) {
    favorable.push(`${sdPlanet} sub-period: positive energy — supports new beginnings`);
  } else if (sdPlanet && sdNature === 'malefic' && caution.length < 3) {
    caution.push(`${sdPlanet} sub-period: some friction ahead — proceed with patience`);
  }

  return {
    favorable: favorable.slice(0, 3),
    caution: caution.slice(0, 3),
    avoid: avoid.slice(0, 2),
  };
}

function InsightsWelcomePanel({
  contextData,
  userName,
  onOutputTypeChange,
  onLifeAreaChange,
  selectedLifeArea,
}: {
  contextData: ChatSourceData;
  userName?: string;
  onOutputTypeChange?: (t: ChatOutputType) => void;
  onLifeAreaChange?: (area: LifeAreaKey) => void;
  selectedLifeArea?: LifeAreaKey | null;
}) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState(0);

  // Advance through stages on cumulative timers; keep scroll at top so user sees content populate
  useEffect(() => {
    const timers: ReturnType<typeof setTimeout>[] = [];
    STAGE_TIMINGS.forEach((ms, idx) => {
      if (idx === 0) return;
      timers.push(setTimeout(() => {
        setStage(idx);
        setTimeout(() => { if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0; }, 60);
      }, ms));
    });
    return () => timers.forEach(clearTimeout);
  }, []);

  const asc = contextData.astroData.asc;
  const moon = contextData.astroData.moon;
  const city = contextData.selectedCity;
  const tz = city?.tz ?? 'UTC';
  const utc = contextData.utcDateTime;
  const lagnaLord = SIGN_LORD_MAP[asc.ascendantSignIndex] ?? 'Unknown';

  let dobStr = 'Unknown', timeStr = 'Unknown';
  if (utc) {
    const d = new Date(utc);
    dobStr = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric', timeZone: tz });
    timeStr = d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone: tz });
  }
  let coordStr = 'Unknown';
  if (city) {
    const latDir = city.lat >= 0 ? 'N' : 'S';
    const lonDir = city.lon >= 0 ? 'E' : 'W';
    coordStr = `${Math.abs(city.lat).toFixed(2)}\u00b0${latDir}, ${Math.abs(city.lon).toFixed(2)}\u00b0${lonDir}`;
  }

  const firstName = (userName ?? '').split(' ')[0];
  const hour = new Date().getHours();
  const timeGreeting = hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : 'Good Evening';
  const greetingEmoji = hour < 12 ? '🌅' : hour < 17 ? '☀️' : '🌙';

  // Fade-in style applied when an element becomes visible
  const pop = {
    '@keyframes wPop': { from: { opacity: 0, transform: 'translateY(6px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
    animation: 'wPop 0.42s ease both',
  };

  const personalRows: [string, string, string, string][] = [
    ['Full Name', userName ?? 'Unknown', 'Date of Birth', dobStr],
    ['Time of Birth', timeStr, 'Place of Birth', city?.label ?? 'Unknown'],
    ['Coordinates', coordStr, 'Timezone', tz],
    ['Ayanamsa', contextData.ayanamsa ?? 'Lahiri', '', ''],
  ];

  const chartRows: [string, string, string, string][] = [
    ['Ascendant (Lagna)', asc.ascendantSign, 'Moon Sign (Rashi)', moon.moonSign],
    ['Lagna Lord', lagnaLord, 'Moon Nakshatra', moon.nakshatra],
    ['Lagna Nakshatra', asc.ascendantNakshatra, '', ''],
  ];

  const tiles: { icon: React.ReactNode; color: string; label: string; tip: string; type: ChatOutputType }[] = [
    { icon: <TimelineIcon sx={{ fontSize: 15 }} />, color: '#7c3aed', label: 'Current Period', tip: 'MD, AD, PD, SD dasha progress', type: 'current_period' },
    { icon: <AssessmentIcon sx={{ fontSize: 15 }} />, color: '#059669', label: 'SWOT Analysis', tip: 'Strengths & areas needing attention', type: 'swot' },
    { icon: <GridViewIcon sx={{ fontSize: 15 }} />, color: '#d97706', label: 'House Dashboard', tip: 'All 12 house scores at a glance', type: 'house_dashboard' },
  ];

  // Shared info-row renderer
  const InfoRow = ({ l1, v1, l2, v2, isLast }: { l1: string; v1: string; l2: string; v2: string; isLast: boolean }) => (
    <>
      {l1 ? (
        <Box sx={{ px: 1, py: 0.625, borderBottom: !isLast ? '1px solid' : 'none', borderColor: 'divider' }}>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.6rem', mb: 0.1 }}>{l1}</Typography>
          <Typography level="body-xs" sx={{ fontWeight: 600, fontSize: '0.71rem', wordBreak: 'break-word', lineHeight: 1.35 }}>{v1}</Typography>
        </Box>
      ) : <Box sx={{ borderBottom: !isLast ? '1px solid' : 'none', borderColor: 'divider' }} />}
      {l2 ? (
        <Box sx={{ px: 1, py: 0.625, borderLeft: '1px solid', borderBottom: !isLast ? '1px solid' : 'none', borderColor: 'divider' }}>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.6rem', mb: 0.1 }}>{l2}</Typography>
          <Typography level="body-xs" sx={{ fontWeight: 600, fontSize: '0.71rem', wordBreak: 'break-word', lineHeight: 1.35 }}>{v2}</Typography>
        </Box>
      ) : <Box sx={{ borderLeft: '1px solid', borderBottom: !isLast ? '1px solid' : 'none', borderColor: 'divider' }} />}
    </>
  );

  // How many personal rows are currently visible (stages 4-7)
  const piRowsVisible = Math.max(0, Math.min(4, stage - 3));
  // How many chart rows are visible (stages 9-11)
  const ceRowsVisible = Math.max(0, Math.min(3, stage - 8));

  return (
    <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

      {/* STAGE 1 — Greeting */}
      {stage >= 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ...pop }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(255,215,0,0.28) 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            animation: 'logoGlowW 3s ease-in-out infinite',
            '@keyframes logoGlowW': {
              '0%,100%': { boxShadow: '0 0 10px rgba(212,175,55,0.4)' },
              '50%': { boxShadow: '0 0 22px rgba(255,215,0,0.7)' }
            }
          }}>
            <img src="/Logo.png" alt="" style={{ width: 26, height: 26, borderRadius: '50%' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', color: '#c2185b', lineHeight: 1.3 }}>
              {timeGreeting}{firstName ? `, ${firstName}` : ''}! {greetingEmoji}
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.68rem' }}>
              Your chart is loaded &amp; personalized
            </Typography>
          </Box>
        </Box>
      )}

      {/* STAGE 2 — Loading bar + message */}
      {stage >= 2 && (
        <Box sx={{ ...pop }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.625, mb: 0.75 }}>
            <Box component="span" sx={{
              fontSize: '0.85rem',
              animation: 'shimmerStar 1.2s ease-in-out infinite',
              '@keyframes shimmerStar': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 1 } }
            }}>✨</Box>
            <Typography level="body-xs" sx={{ color: '#7c3aed', fontStyle: 'italic', fontSize: '0.7rem' }}>
              {stage < 12 ? 'Analyzing your chart & loading insights...' : 'Insights ready — explore below'}
            </Typography>
          </Box>
          {/* Animated progress bar — runs from stage 2 until stage 12 */}
          {stage < 12 && (
            <Box sx={{
              height: 3, borderRadius: '999px', overflow: 'hidden',
              bgcolor: 'rgba(124,58,237,0.12)'
            }}>
              <Box sx={{
                height: '100%',
                background: 'linear-gradient(90deg, #7c3aed, #a855f7, #7c3aed)',
                backgroundSize: '200% 100%',
                borderRadius: '999px',
                animation: 'scanBar 1.6s linear infinite',
                '@keyframes scanBar': {
                  '0%': { backgroundPosition: '100% 0' },
                  '100%': { backgroundPosition: '-100% 0' }
                }
              }} />
            </Box>
          )}
        </Box>
      )}

      {/* Weekly Briefing — loads with stage 3, sits above Personal Information */}
      {stage >= 3 && (() => {
        const briefing = computeWeeklyBriefing(contextData);
        const total = briefing.favorable.length + briefing.caution.length + briefing.avoid.length;
        if (total === 0) return null;
        return (
          <Box sx={{ border: '1px solid', borderColor: 'rgba(212,175,55,0.3)', borderRadius: 'md', overflow: 'hidden', ...pop }}>
            <Box sx={{
              px: 1.25, py: 0.5,
              background: 'linear-gradient(90deg, rgba(212,175,55,0.1) 0%, rgba(184,48,80,0.05) 100%)',
              borderBottom: '1px solid', borderColor: 'rgba(212,175,55,0.2)',
              display: 'flex', alignItems: 'center', gap: 0.5,
            }}>
              <Typography level="body-xs" sx={{
                fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.07em',
                color: '#7a4f00', fontSize: '0.62rem',
              }}>
                📅 This Week's Briefing
              </Typography>
              <Box sx={{ flex: 1 }} />
              {onLifeAreaChange && (() => {
                // Welcome panel has no "current" area — always start from the beginning
                const nextArea = LIFE_AREA_NAV_ORDER[0];
                const nextLabel = LIFE_AREAS[nextArea]?.label ?? nextArea;
                return (
                  <Tooltip title={nextLabel} size="sm" placement="left">
                    <Box
                      component="button"
                      onClick={() => { onLifeAreaChange(nextArea); onOutputTypeChange?.('life_area'); }}
                      sx={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        border: '1px solid rgba(212,175,55,0.4)',
                        background: 'rgba(212,175,55,0.09)', cursor: 'pointer', outline: 'none',
                        color: '#7a4f00', p: 0,
                        '&:hover': { background: 'rgba(212,175,55,0.22)', borderColor: 'rgba(212,175,55,0.7)' },
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: '0.6rem' }} />
                    </Box>
                  </Tooltip>
                );
              })()}
            </Box>
            <Box sx={{ px: 1.25, py: 0.875, display: 'flex', flexDirection: 'column', gap: 0.55 }}>
              {briefing.favorable.map((item, i) => (
                <Box key={`fav${i}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ fontSize: '0.72rem', flexShrink: 0, lineHeight: 1.5 }}>✅</Box>
                  <Typography level="body-xs" sx={{ color: '#1a5c38', fontSize: '0.7rem', lineHeight: 1.5 }}>{item}</Typography>
                </Box>
              ))}
              {briefing.caution.map((item, i) => (
                <Box key={`cau${i}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ fontSize: '0.72rem', flexShrink: 0, lineHeight: 1.5 }}>⚠️</Box>
                  <Typography level="body-xs" sx={{ color: '#7a4f00', fontSize: '0.7rem', lineHeight: 1.5 }}>{item}</Typography>
                </Box>
              ))}
              {briefing.avoid.map((item, i) => (
                <Box key={`avd${i}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                  <Box component="span" sx={{ fontSize: '0.72rem', flexShrink: 0, lineHeight: 1.5 }}>🚫</Box>
                  <Typography level="body-xs" sx={{ color: '#8b2020', fontSize: '0.7rem', lineHeight: 1.5 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
            {/* Bell note — footer of briefing card */}
            <Box sx={{ px: 1.25, py: 0.5, borderTop: '1px solid', borderColor: 'rgba(212,175,55,0.2)', background: 'rgba(212,175,55,0.04)' }}>
              <Typography level="body-xs" sx={{ textAlign: 'center', color: 'text.tertiary', fontSize: '0.6rem' }}>
                Tap 🔔 above for detailed predictions &amp; alerts
              </Typography>
            </Box>
          </Box>
        );
      })()}

      {/* STAGE 3+ — Personal Information card */}
      {stage >= 3 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'md', overflow: 'hidden', ...(stage === 3 ? pop : {}) }}>
          <Box sx={{ px: 1.25, py: 0.5, bgcolor: 'background.level2', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary', fontSize: '0.63rem' }}>
              Personal Information
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            {personalRows.slice(0, piRowsVisible).map(([l1, v1, l2, v2], ri) => (
              <React.Fragment key={ri}>
                <InfoRow l1={l1} v1={v1} l2={l2} v2={v2} isLast={ri === piRowsVisible - 1} />
              </React.Fragment>
            ))}
            {/* Placeholder shimmer row while more rows are loading */}
            {piRowsVisible < 4 && (
              <>
                <Box sx={{ px: 1, py: 0.75, borderBottom: piRowsVisible < 3 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Box sx={{ height: 8, width: '60%', borderRadius: 4, bgcolor: 'neutral.200', animation: 'rowShimmer 1.2s ease-in-out infinite', '@keyframes rowShimmer': { '0%,100%': { opacity: 0.4 }, '50%': { opacity: 0.9 } } }} />
                </Box>
                <Box sx={{ px: 1, py: 0.75, borderLeft: '1px solid', borderBottom: piRowsVisible < 3 ? '1px solid' : 'none', borderColor: 'divider' }}>
                  <Box sx={{ height: 8, width: '50%', borderRadius: 4, bgcolor: 'neutral.200', animation: 'rowShimmer 1.2s ease-in-out infinite 0.2s' }} />
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* STAGE 8+ — Chart Essentials card */}
      {stage >= 8 && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'md', overflow: 'hidden', ...(stage === 8 ? pop : {}) }}>
          <Box sx={{ px: 1.25, py: 0.5, bgcolor: 'background.level2', borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'text.secondary', fontSize: '0.63rem' }}>
              ✦ Chart Essentials
            </Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
            {chartRows.slice(0, ceRowsVisible).map(([l1, v1, l2, v2], ri) => (
              <React.Fragment key={ri}>
                <InfoRow l1={l1} v1={v1} l2={l2} v2={v2} isLast={ri === ceRowsVisible - 1} />
              </React.Fragment>
            ))}
            {/* Placeholder shimmer while rows are loading */}
            {ceRowsVisible < 3 && (
              <>
                <Box sx={{ px: 1, py: 0.75 }}>
                  <Box sx={{ height: 8, width: '55%', borderRadius: 4, bgcolor: 'neutral.200', animation: 'rowShimmer 1.2s ease-in-out infinite' }} />
                </Box>
                <Box sx={{ px: 1, py: 0.75, borderLeft: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ height: 8, width: '45%', borderRadius: 4, bgcolor: 'neutral.200', animation: 'rowShimmer 1.2s ease-in-out infinite 0.15s' }} />
                </Box>
              </>
            )}
          </Box>
        </Box>
      )}

      {/* STAGE 12 — Quick Insights tiles */}
      {stage >= 12 && (
        <>
          <Divider sx={{ ...pop }}>
            <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', color: 'text.tertiary', fontSize: '0.62rem', px: 0.5 }}>Quick Insights</Typography>
          </Divider>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, ...pop }}>
            {tiles.map(({ icon, color, label, tip, type }) => (
              <Box
                key={label}
                onClick={() => onOutputTypeChange?.(type)}
                sx={{
                  display: 'flex', gap: 1, alignItems: 'flex-start', p: 1.1,
                  borderRadius: 'md', bgcolor: 'background.level1', border: '1px solid', borderColor: 'divider',
                  cursor: 'pointer', transition: 'all 0.18s',
                  '&:hover': { bgcolor: `${color}12`, borderColor: color, transform: 'translateX(3px)' },
                }}
              >
                <Box sx={{ color, mt: 0.15, flexShrink: 0 }}>{icon}</Box>
                <Box>
                  <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color, lineHeight: 1.35 }}>{label}</Typography>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.67rem', lineHeight: 1.4 }}>{tip}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </>
      )}

      {/* Scroll anchor */}
      <div ref={bottomRef} />
    </Box>
  );
}

// ---
// LifeAreaGuidancePanel — shown in col 3 for every life area
// ---

interface PersonalAreaMeta {
  icon: string;
  title: string;
  color: string;
  feedKeys: string[];
  dosItems: string[];
  avoidItems: string[];
}

const PERSONAL_AREA_META: Partial<Record<LifeAreaKey, PersonalAreaMeta>> = {
  self: {
    icon: '🧘',
    title: 'Self & Personality',
    color: '#0e7490',
    feedKeys: ['Self', 'self'],
    dosItems: [
      'Set clear weekly intentions that align with your lagna lord energy',
      'Build morning routines that reinforce physical vitality and mental clarity',
      'Channel your natural strengths — focus on authentic self-expression',
    ],
    avoidItems: [
      'Impulsive decisions when the lagna lord is weak or under affliction',
      'Ego-driven conflicts that drain vital energy unnecessarily',
      'Neglecting your physical health and daily discipline',
    ],
  },
  health: {
    icon: '💚',
    title: 'Health & Vitality',
    color: '#065f46',
    feedKeys: ['Health', 'AccidentRisk', 'health'],
    dosItems: [
      'Maintain consistent exercise, adequate sleep and daily hydration',
      'Schedule health check-ups during favorable Jupiter or Venus periods',
      'Strengthen immunity with mindful diet and active stress management',
    ],
    avoidItems: [
      'Elective surgeries when Mars or the 8th house lord is afflicted',
      'Overexertion and skipping rest near eclipses or during combustion',
      'Ignoring recurring symptoms — early attention prevents complications',
    ],
  },
  education: {
    icon: '📚',
    title: 'Education & Learning',
    color: '#3730a3',
    feedKeys: ['Education', 'Knowledge', 'education'],
    dosItems: [
      'Begin new courses or certifications during Mercury-strong periods',
      'Engage in focused reading, structured research and quality discussion',
      'Revisit past subjects to deepen mastery before advancing further',
    ],
    avoidItems: [
      'Starting new study programs or enrolling courses during Mercury retrograde',
      'Scattering focus across too many subjects simultaneously',
      'Academic commitments when 5th or 9th house lords are weakened',
    ],
  },
  wealth: {
    icon: '💰',
    title: 'Wealth & Finances',
    color: '#92400e',
    feedKeys: ['Wealth', 'Finance', 'wealth', 'finance'],
    dosItems: [
      'Save and invest systematically during Jupiter-favorable dasha periods',
      'Diversify income streams when 11th house lord is strong',
      'Track expenses diligently and set clear financial goals monthly',
    ],
    avoidItems: [
      'Speculative investments when the 2nd or 11th house lord is afflicted',
      'Lending large sums or giving guarantees during Saturn debilitation',
      'Impulsive luxury spending during Rahu dasha or eclipses',
    ],
  },
  job: {
    icon: '💼',
    title: 'Career & Job',
    color: '#1e40af',
    feedKeys: ['Career', 'Job', 'job', 'career', 'Profession'],
    dosItems: [
      'Build professional networks and visibility during Mercury-Jupiter periods',
      'Take on leadership roles and extra responsibilities when Sun is strong',
      'Upskill and pursue certifications to position for growth opportunities',
    ],
    avoidItems: [
      'Switching jobs or roles during Saturn retrograde without careful evaluation',
      'Confrontations with authority figures when Sun is afflicted',
      'Signing employment contracts during Mercury retrograde',
    ],
  },
  business: {
    icon: '🏢',
    title: 'Business & Entrepreneurship',
    color: '#7c3aed',
    feedKeys: ['Business', 'business', 'Entrepreneurship'],
    dosItems: [
      'Launch new ventures or products during Jupiter-expansion dasha periods',
      'Formalise partnerships and agreements when Venus is strong',
      'Invest in marketing and brand-building during Mercury-favorable windows',
    ],
    avoidItems: [
      'Major capital investments when Rahu or Ketu transit the 7th or 10th house',
      'Signing partnership deeds or MOUs during Mercury retrograde',
      'Aggressive expansion when Saturn is contracting the 10th house',
    ],
  },
  marriage: {
    icon: '💑',
    title: 'Marriage & Relationships',
    color: '#be185d',
    feedKeys: ['Marriage', 'Relationships', 'marriage', 'Partnership', 'Love'],
    dosItems: [
      'Nurture open communication and quality time during Venus-strong periods',
      'Plan romantic milestones or proposals when 7th house lord is well-placed',
      'Resolve differences with patience and empathy during Venus dasha',
    ],
    avoidItems: [
      'Major relationship decisions when Mars afflicts the 7th house',
      'Confrontations or ultimatums during Venus combustion periods',
      'Rushing into commitments when Saturn retrogrades through the 7th house',
    ],
  },
  children: {
    icon: '👶',
    title: 'Children & Progeny',
    color: '#047857',
    feedKeys: ['Children', 'children', 'Progeny'],
    dosItems: [
      'Plan conception or fertility treatments when Jupiter transits the 5th house',
      'Support children\'s education and creative growth during Mercury periods',
      'Create a nurturing home environment when Moon and 4th lord are strong',
    ],
    avoidItems: [
      'Elective 5th-house procedures when Rahu afflicts that house',
      'High-stress family environments when the 5th house lord is debilitated',
      'Ignoring children\'s health signals when the 5th lord is under combustion',
    ],
  },
  property: {
    icon: '🏠',
    title: 'Property & Real Estate',
    color: '#b45309',
    feedKeys: ['Property', 'property', 'RealEstate', 'Home'],
    dosItems: [
      'Purchase or register property during Jupiter-Venus favorable windows',
      'Plan renovations and interiors when Mars and 4th lord are well-placed',
      'Conduct due diligence and legal checks when Mercury is direct and strong',
    ],
    avoidItems: [
      'Finalising property deals when the 4th house lord is afflicted or debilitated',
      'Signing sale deeds during Saturn\'s 8th transit from Moon sign',
      'Major structural renovations when Rahu is transiting the 4th house',
    ],
  },
  mother: {
    icon: '🌸',
    title: 'Mother & Home Life',
    color: '#9d174d',
    feedKeys: ['Mother', 'mother', 'Parents'],
    dosItems: [
      'Spend quality time with your mother during Moon-strong or Cancer transits',
      'Attend proactively to her health and comfort during 4th lord favorable periods',
      'Strengthen domestic harmony and home routines when Moon is well-aspected',
    ],
    avoidItems: [
      'Neglecting mother\'s well-being when Moon is afflicted by Saturn or Rahu',
      'Family conflicts and domestic disputes during 4th lord transit through dusthanas',
      'Delaying medical attention for your mother during Moon combustion',
    ],
  },
  father: {
    icon: '👨‍👦',
    title: 'Father & Mentors',
    color: '#1e3a5f',
    feedKeys: ['Father', 'father', 'Parents'],
    dosItems: [
      'Honor and seek guidance from your father or mentors during Sun-strong periods',
      'Strengthen bonds with authority figures when the 9th house lord is favorable',
      'Focus on higher learning, dharma and spiritual growth in 9th house periods',
    ],
    avoidItems: [
      'Conflicts with father or authority figures when Sun is afflicted or combust',
      'Disrespecting senior guidance when the 9th house lord transits dusthanas',
      'Delaying important conversations with your father during Sun-Saturn tensions',
    ],
  },
};

const COMBUST_PERSONAL_SIGNALS: Record<string, Partial<Record<LifeAreaKey, string>>> = {
  Mercury: {
    self:      'Mercury combust — review communications carefully, avoid snap decisions',
    health:    'Mercury combust — re-check medical instructions before acting',
    education: 'Mercury combust — avoid critical exams or new enrollments this week',
    job:       'Mercury combust — defer contract signings and important negotiations',
    business:  'Mercury combust — review agreements carefully; avoid new partnerships',
    wealth:    'Mercury combust — double-check financial documents and transactions',
  },
  Mars: {
    self:     'Mars combust — manage physical energy carefully, avoid overreactions',
    health:   'Mars combust — skip intense physical training; risk of strain is higher',
    property: 'Mars combust — pause renovation or construction decisions this week',
    business: 'Mars combust — avoid aggressive expansion or confrontational moves',
  },
  Moon: {
    self:     'Moon combust — emotional fluctuations are heightened; allow extra reflection',
    health:   'Moon combust — sleep and immunity may be affected; prioritise rest',
    mother:   "Moon combust — be especially attentive to your mother's well-being",
    marriage: 'Moon combust — emotional sensitivity is high; avoid reactionary responses',
  },
  Venus: {
    marriage: 'Venus combust — harmony in relationships may feel strained; be patient',
    wealth:   'Venus combust — avoid luxury purchases or lifestyle inflation this week',
    business: 'Venus combust — partnership dynamics may be tense; defer decisions',
  },
  Jupiter: {
    wealth:   'Jupiter combust — avoid major financial commitments or expansions',
    children: 'Jupiter combust — 5th house significations are weakened; extra care needed',
    education:'Jupiter combust — guidance from teachers may be unclear; verify sources',
  },
  Sun: {
    job:    'Sun combust — visibility and authority are reduced; avoid direct confrontations',
    father: "Sun combust — father's health or relationship may need extra attention",
  },
  Saturn: {
    property: 'Saturn combust — property delays and legal complications are more likely',
    job:      'Saturn combust — career obstacles may arise; maintain steady discipline',
  },
};

// ── Life-area → relevant houses, Jaimini karaka key, primary divisional chart ──────────────
const LIFE_AREA_ASTRO_CONFIG: Record<LifeAreaKey, { houses: number[]; karakaKey: string; varga: string | null }> = {
  self:      { houses: [1, 5, 9],   karakaKey: 'AK',  varga: 'D9'  },
  health:    { houses: [1, 6, 8],   karakaKey: 'AK',  varga: null  },
  education: { houses: [4, 5, 9],   karakaKey: 'AmK', varga: 'D24' },
  wealth:    { houses: [2, 11, 5],  karakaKey: 'AK',  varga: 'D2'  },  // No Jaimini Dhana-karaka; AK (overall karmic success) is most defensible
  job:       { houses: [10, 6, 2],  karakaKey: 'AmK', varga: 'D10' },
  business:  { houses: [10, 7, 11], karakaKey: 'AmK', varga: 'D10' },
  marriage:  { houses: [7, 2, 11],  karakaKey: 'DK',  varga: 'D9'  },
  children:  { houses: [5, 9, 1],   karakaKey: 'PuK', varga: 'D7'  },
  property:  { houses: [4, 11, 12], karakaKey: 'MK',  varga: 'D4'  },
  mother:    { houses: [4, 2, 12],  karakaKey: 'MK',  varga: 'D4'  },
  father:    { houses: [9, 10, 5],  karakaKey: 'GK',  varga: 'D12' }, // GK (Gnātikāraka) = ancestral/paternal lineage in several Jaimini schools
};

const KARAKA_LABELS: Record<string, string> = {
  AK: 'Ātmakāraka', AmK: 'Amātyakāraka', DK: 'Dārakāraka',
  PuK: 'Putrakāraka', MK: 'Mātṛkāraka', BK: 'Bhrātṛkāraka', GK: 'Gnātikāraka',
};

/** Planet own-signs and exalt signs (1-indexed) for varga dignity check */
const PLANET_OWN_SIGNS: Record<string, number[]> = {
  Sun: [5], Moon: [4], Mars: [1, 8], Mercury: [3, 6],
  Jupiter: [9, 12], Venus: [2, 7], Saturn: [10, 11],
};
const PLANET_EXALT_SIGN: Record<string, number> = {
  Sun: 1, Moon: 2, Mars: 10, Mercury: 6, Jupiter: 4, Venus: 12, Saturn: 7,
};

/** Compute Jaimini Chara Karakas from natal grahas (degree-within-sign descending). */
function deriveCharaKarakas(grahas: Partial<Record<string, { lon: number }>>): Record<string, string> {
  const seven = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  const ranked = seven
    .filter(p => grahas[p])
    .map(p => ({ planet: p, deg: (grahas[p]!.lon % 30 + 30) % 30 }))
    .sort((a, b) => b.deg - a.deg);
  const keys = ['AK', 'AmK', 'BK', 'MK', 'PuK', 'GK', 'DK'];
  const result: Record<string, string> = {};
  keys.forEach((k, i) => { if (ranked[i]) result[k] = ranked[i].planet; });
  return result;
}

/**
 * Build personalized "Things to Do" and "Things to Avoid" from live chart data:
 *   1. Feed-derived items (dasha+transit+natal already scored per life area)
 *   2. Dasha lord activation of area houses (SD → PD → MD)
 *   3. Jaimini karaka planet strength (combust / debil / strong)
 *   4. Primary house combined score (D1 planet impact + Bhava Bala)
 *   5. Pre-computed transit & dasha impact per house
 *   6. Divisional chart (varga) karaka placement
 * Falls back to static meta items when live signals are insufficient.
 */
function buildPersonalizedGuidance(
  ctx: ChatSourceData,
  lifeArea: LifeAreaKey,
  fallback: PersonalAreaMeta,
): { dosItems: string[]; avoidItems: string[] } {
  const cfg = LIFE_AREA_ASTRO_CONFIG[lifeArea];
  const dos: string[] = [];
  const avoids: string[] = [];

  const now     = new Date();
  const horizon = new Date(now);
  horizon.setDate(now.getDate() + 90);

  // ── 1. FEED-DERIVED ─────────────────────────────────────────────────────
  const cleanMsg = (f: any): string =>
    ((f.shortMessage ?? f.message ?? '') as string)
      .replace(/^[A-Z0-9][A-Z0-9 _&\-]+:\s*/, '')
      .replace(/\s+/g, ' ')
      .trim();

  const areaFeeds = (ctx.feeds ?? []).filter((f: any) => {
    const fk = (f.lifeArea ?? f.life_area ?? '').toLowerCase();
    if (!fallback.feedKeys.some(k => fk.includes(k.toLowerCase()))) return false;
    try {
      const from = new Date(f.window?.from ?? f.window?.to ?? now);
      const to   = new Date(f.window?.to   ?? f.window?.from ?? now);
      return from <= horizon && to >= now;
    } catch { return false; }
  }).sort((a: any, b: any) => Math.abs(b.score ?? 0) - Math.abs(a.score ?? 0));

  for (const f of areaFeeds) {
    const sc  = (f.score ?? 0) as number;
    const txt = cleanMsg(f);
    if (!txt) continue;
    if (sc >= 40 && dos.length    < 2) dos.push(txt);
    if (sc <= -30 && avoids.length < 2) avoids.push(txt);
  }

  // ── 2. DASHA LORD ACTIVATION ────────────────────────────────────────────
  const dc  = ctx.dashaContext as any;
  const fn  = ctx.functionalNature as Partial<Record<string, string>> | undefined;
  const getNat = (p: string) =>
    fn?.[p] === 'benefic' || fn?.[p] === 'yoga_karaka' ? 'benefic'
    : fn?.[p] === 'malefic' ? 'malefic' : 'neutral';

  const lvArr = dc?.levels as Array<{ level: string; planet: string; occupies: number; rules: number[] }> | undefined;
  const getLevel = (lv: string) => lvArr?.find((l: any) => l.level === lv) ?? dc?.[lv];
  const hitsArea = (lv: any) => {
    if (!lv) return false;
    const rules   = (lv.rules   ?? []) as number[];
    const aspects = (lv.aspects ?? []) as number[];
    return cfg.houses.some(h => rules.includes(h) || lv.occupies === h || aspects.includes(h));
  };

  const tryDashaLevel = (lv: any, label: string) => {
    if (!lv || !hitsArea(lv)) return;
    const nat   = getNat(lv.planet);
    const aName = fallback.title.toLowerCase();
    if (nat === 'benefic'  && dos.length    < 3)
      dos.push(`${lv.planet} ${label} activates ${aName} houses — capitalize on this favorable window`);
    if (nat === 'malefic'  && avoids.length < 3)
      avoids.push(`${lv.planet} ${label} pressures ${aName} houses — proceed with patience and deliberation`);
  };

  tryDashaLevel(getLevel('SD'), 'sub-sub-period');
  tryDashaLevel(getLevel('PD'), 'pratyantar');
  tryDashaLevel(getLevel('MD'), 'mahadasha');

  // ── 3. JAIMINI KARAKA STRENGTH ──────────────────────────────────────────
  const karakas  = deriveCharaKarakas(ctx.astroData?.grahas ?? {});
  const kPlanet  = karakas[cfg.karakaKey];
  if (kPlanet) {
    const kPct     = (ctx.blendedPlanetPct as any)?.[kPlanet] as number | undefined;
    const combust  = (ctx.combust as any)?.[kPlanet]?.combust === true;
    const debil    = (ctx.debil   as any)?.[kPlanet] === true;
    const exalt    = (ctx.exalt   as any)?.[kPlanet] === true;
    const kLabel   = KARAKA_LABELS[cfg.karakaKey] ?? cfg.karakaKey;
    const aName    = fallback.title.toLowerCase();

    if (!combust && !debil && (exalt || (kPct !== undefined && kPct > 65)) && dos.length < 3)
      dos.push(`Your ${kLabel} ${kPlanet} is strong — its significations actively support ${aName}`);
    else if ((combust || debil || (kPct !== undefined && kPct < 25)) && avoids.length < 3)
      avoids.push(`${kLabel} ${kPlanet} is ${combust ? 'combust' : debil ? 'debilitated' : 'weak'} — handle ${aName} matters with extra care`);
  }

  // ── 4. PRIMARY HOUSE COMBINED SCORE ─────────────────────────────────────
  const pH  = cfg.houses[0];
  const hSc = ((ctx.d1HouseScores  ?? {}) as Record<number, number>)[pH] ?? 0;
  const bSc = ((ctx.bhavaBalaRupas ?? {}) as Record<number, number>)[pH] ?? 50;
  const comb = (hSc + bSc) / 2;
  if (comb >= 68 && dos.length    < 3)
    dos.push(`House ${pH} is strong in your chart — a solid foundation supports ${fallback.title.toLowerCase()} endeavours`);
  else if (comb < 30 && avoids.length < 3)
    avoids.push(`House ${pH} needs strengthening — focus on steady effort rather than forcing outcomes in ${fallback.title.toLowerCase()}`);

  // ── 5. TRANSIT & DASHA IMPACT BY HOUSE ──────────────────────────────────
  const tByH = (ctx.transitImpactByHouse ?? {}) as Record<number, number>;
  const dByH = (ctx.dashaImpactByHouse   ?? {}) as Record<number, number>;
  const tScore = tByH[pH];
  const dScore = dByH[pH];
  if (tScore !== undefined) {
    if (tScore > 62  && dos.length    < 3)
      dos.push(`Current transits are supportive of House ${pH} — a good window to advance ${fallback.title.toLowerCase()} goals`);
    else if (tScore < 38 && avoids.length < 3)
      avoids.push(`Transits are creating friction on House ${pH} — avoid forcing ${fallback.title.toLowerCase()} decisions right now`);
  }
  if (dScore !== undefined) {
    if (dScore > 62 && dos.length < 3)
      dos.push(`Dasha energy is well-aligned with House ${pH} — this period supports ${fallback.title.toLowerCase()} progress`);
    else if (dScore < 38 && avoids.length < 3)
      avoids.push(`Dasha lords are reducing flow to House ${pH} — conserve energy and wait for a clearer window`);
  }

  // ── 6. DIVISIONAL CHART — karaka in own/exalt sign ──────────────────────
  if (cfg.varga && ctx.vargaData && kPlanet && dos.length < 3) {
    const vd = (ctx.vargaData as any)[cfg.varga];
    const vSign = vd?.planetSigns?.[kPlanet] as number | undefined;
    if (vSign !== undefined) {
      const own   = PLANET_OWN_SIGNS[kPlanet] ?? [];
      const exSgn = PLANET_EXALT_SIGN[kPlanet];
      if (own.includes(vSign) || vSign === exSgn)
        dos.push(`${kPlanet} is well-placed in ${cfg.varga} (${fallback.title.toLowerCase()} divisional chart) — a strong deeper-level indicator`);
    }
  }

  // ── FALLBACK to static items if not enough signals ───────────────────────
  while (dos.length    < 2) dos.push(fallback.dosItems[dos.length]       ?? 'Act purposefully during favorable dasha windows');
  while (avoids.length < 2) avoids.push(fallback.avoidItems[avoids.length] ?? 'Avoid hasty moves when key house lords are under pressure');

  return { dosItems: dos.slice(0, 3), avoidItems: avoids.slice(0, 3) };
}

export function PersonalLifeAreaPanel({
  contextData,
  lifeArea,
  mobile,
  onLifeAreaChange,
  onOutputTypeChange,
}: {
  contextData: ChatSourceData;
  lifeArea: LifeAreaKey;
  mobile?: boolean;
  onLifeAreaChange?: (area: LifeAreaKey) => void;
  onOutputTypeChange?: (t: ChatOutputType) => void;
}) {
  const meta = PERSONAL_AREA_META[lifeArea];
  if (!meta) return null;

  // Feed signals for this area in the next 7 days
  const now = new Date();
  const weekEnd = new Date(now);
  weekEnd.setDate(now.getDate() + 7);

  const areaFeeds = (contextData.feeds ?? []).filter((f: any) => {
    const win = f.window;
    if (!win) return false;
    const fk = (f.lifeArea ?? '') as string;
    if (!meta.feedKeys.some(k => k.toLowerCase() === fk.toLowerCase())) return false;
    try {
      const from = new Date(win.from ?? win.to ?? win.start);
      const to   = new Date(win.to ?? win.from ?? win.end);
      return from <= weekEnd && to >= now;
    } catch { return false; }
  });

  const favorable: string[] = [];
  const caution: string[] = [];
  const avoidDynamic: string[] = [];

  // Feed-based signals
  const sorted = [...areaFeeds].sort((a: any, b: any) => Math.abs(b.score ?? 0) - Math.abs(a.score ?? 0));
  for (const f of sorted) {
    const score = (f as any).score ?? 0;
    const sm    = (f as any).shortMessage as string | undefined;
    const text  = sm?.replace(/^[A-Z0-9][A-Z0-9 _&\-]+:\s*/, '') ?? '';
    if (score >= 30 && favorable.length < 3)
      favorable.push(text || 'Favorable period — good time for positive progress');
    else if (score <= -25 && caution.length < 3)
      caution.push(text || 'Needs careful handling — proceed with patience');
  }

  // Combustion signals
  for (const [planet, areaMap] of Object.entries(COMBUST_PERSONAL_SIGNALS)) {
    if ((contextData.combust as any)?.[planet]?.combust) {
      const signal = areaMap[lifeArea];
      if (signal) avoidDynamic.push(signal);
    }
  }

  // Retrograde signals — uses natal grahas retrograde flag (natal Rx = permanent chart trait with lifelong guidance implications)
  const natalGrahas = contextData.astroData?.grahas ?? {};
  if (lifeArea === 'education' && (natalGrahas as any)['Mercury']?.retrograde)
    avoidDynamic.push('Mercury retrograde natally — reviewing and iterating before committing to new courses serves you better');
  if (lifeArea === 'health' && (natalGrahas as any)['Mars']?.retrograde)
    avoidDynamic.push('Mars retrograde natally — pace energy deliberately; avoid forcing aggressive physical routines');
  if (lifeArea === 'self' && (natalGrahas as any)['Saturn']?.retrograde)
    avoidDynamic.push('Saturn retrograde natally — internalize its lessons; revisit long-term commitments before making structural changes');

  // SD dasha signal
  const fn      = contextData.functionalNature as Partial<Record<string, string>> | undefined;
  const levels  = (contextData.dashaContext as any)?.levels as Array<{ level: string; planet: string }> | undefined;
  const sdPlanet = levels?.find((l: any) => l.level === 'SD')?.planet ?? (contextData.dashaContext as any)?.SD?.planet;
  const sdNature = sdPlanet && fn ? fn[sdPlanet] : null;
  if (sdPlanet) {
    const ctx = lifeArea === 'education' ? 'learning and intellectual pursuits'
      : lifeArea === 'health' ? 'vitality and well-being'
      : 'personal growth and self-expression';
    if (sdNature === 'benefic' && favorable.length < 3)
      favorable.push(`${sdPlanet} sub-period: supportive energy for ${ctx}`);
    else if (sdNature === 'malefic' && caution.length < 2)
      caution.push(`${sdPlanet} sub-period: some friction — apply patience and steady effort`);
  }

  const hasSignals = favorable.length > 0 || caution.length > 0;

  // Build personalized do/avoid items from live chart data (feeds, dasha, karaka, house, transit, varga)
  const { dosItems, avoidItems } = buildPersonalizedGuidance(contextData, lifeArea, meta);

  // Font / spacing scale: larger sizes on mobile for readability
  const fs = mobile ? {
    title: '1.05rem', subtitle: '0.78rem', sectionHdr: '0.82rem',
    body: '0.92rem', bodyIcon: '0.92rem', bullet: '0.82rem',
    footer: '0.75rem', noSignals: '0.88rem', arrowIcon: '0.8rem', areaIcon: '1.25rem',
  } : {
    title: '0.82rem', subtitle: '0.62rem', sectionHdr: '0.62rem',
    body: '0.7rem', bodyIcon: '0.72rem', bullet: '0.65rem',
    footer: '0.6rem', noSignals: '0.68rem', arrowIcon: '0.65rem', areaIcon: '1rem',
  };
  const sz = mobile ? { iconCircle: 40, arrowBtn: 30 } : { iconCircle: 34, arrowBtn: 26 };
  const sp = mobile
    ? { hpx: 1.5, hpy: 0.75, bpy: 1.1, bgap: 0.875 }
    : { hpx: 1.25, hpy: 0.5,  bpy: 0.875, bgap: 0.55 };

  return (
    <Box sx={{ p: mobile ? 2 : 1.5, display: 'flex', flexDirection: 'column', gap: 1.25 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.875 }}>
        <Box sx={{
          width: sz.iconCircle, height: sz.iconCircle, borderRadius: '50%', flexShrink: 0,
          background: `linear-gradient(135deg, ${meta.color}20 0%, ${meta.color}12 100%)`,
          border: `1px solid ${meta.color}30`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Typography sx={{ fontSize: fs.areaIcon, lineHeight: 1 }}>{meta.icon}</Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, fontSize: fs.title, color: '#b83050', lineHeight: 1.3 }}>
            {meta.title}
          </Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: fs.subtitle }}>
            Personalised insights &amp; guidance for this week
          </Typography>
        </Box>

      </Box>

      {/* Notifications — feed signals */}
      {hasSignals ? (
        <Box sx={{ border: '1px solid', borderColor: 'rgba(212,175,55,0.3)', borderRadius: 'md', overflow: 'hidden' }}>
          <Box sx={{
            px: sp.hpx, py: sp.hpy,
            background: 'linear-gradient(90deg, rgba(212,175,55,0.1) 0%, rgba(184,48,80,0.05) 100%)',
            borderBottom: '1px solid', borderColor: 'rgba(212,175,55,0.2)',
            display: 'flex', alignItems: 'center', gap: 0.5,
          }}>
            <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#7a4f00', fontSize: fs.sectionHdr }}>
              🔔 This Week's Notifications
            </Typography>
          </Box>
          <Box sx={{ px: sp.hpx, py: sp.bpy, display: 'flex', flexDirection: 'column', gap: sp.bgap }}>
            {favorable.map((item, i) => (
              <Box key={`fav${i}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                <Box component="span" sx={{ fontSize: fs.bodyIcon, flexShrink: 0, lineHeight: 1.5 }}>✅</Box>
                <Typography level="body-xs" sx={{ color: '#1a5c38', fontSize: fs.body, lineHeight: 1.5 }}>{item}</Typography>
              </Box>
            ))}
            {caution.map((item, i) => (
              <Box key={`cau${i}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                <Box component="span" sx={{ fontSize: fs.bodyIcon, flexShrink: 0, lineHeight: 1.5 }}>⚠️</Box>
                <Typography level="body-xs" sx={{ color: '#7a4f00', fontSize: fs.body, lineHeight: 1.5 }}>{item}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 'md', px: sp.hpx, py: 1, textAlign: 'center' }}>
          <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: fs.noSignals, fontStyle: 'italic' }}>
            No active signals for this area this week — a steady period ahead.
          </Typography>
        </Box>
      )}

      {/* Things to Do */}
      <Box sx={{ border: '1px solid', borderColor: 'rgba(16,185,129,0.3)', borderRadius: 'md', overflow: 'hidden' }}>
        <Box sx={{ px: sp.hpx, py: sp.hpy, background: 'linear-gradient(90deg, rgba(16,185,129,0.1) 0%, rgba(5,150,105,0.04) 100%)', borderBottom: '1px solid', borderColor: 'rgba(16,185,129,0.2)' }}>
          <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#065f46', fontSize: fs.sectionHdr }}>
            ✦ Things to Do
          </Typography>
        </Box>
        <Box sx={{ px: sp.hpx, py: sp.bpy, display: 'flex', flexDirection: 'column', gap: sp.bgap }}>
          {dosItems.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Box component="span" sx={{ color: '#065f46', fontWeight: 700, fontSize: fs.bullet, flexShrink: 0, lineHeight: 1.7 }}>▸</Box>
              <Typography level="body-xs" sx={{ color: '#1a5c38', fontSize: fs.body, lineHeight: 1.5 }}>{item}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Things to Avoid */}
      <Box sx={{ border: '1px solid', borderColor: 'rgba(239,68,68,0.25)', borderRadius: 'md', overflow: 'hidden' }}>
        <Box sx={{ px: sp.hpx, py: sp.hpy, background: 'linear-gradient(90deg, rgba(239,68,68,0.08) 0%, rgba(220,38,38,0.03) 100%)', borderBottom: '1px solid', borderColor: 'rgba(239,68,68,0.18)' }}>
          <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: '#991b1b', fontSize: fs.sectionHdr }}>
            ⊘ Things to Avoid
          </Typography>
        </Box>
        <Box sx={{ px: sp.hpx, py: sp.bpy, display: 'flex', flexDirection: 'column', gap: sp.bgap }}>
          {avoidItems.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Box component="span" sx={{ color: '#991b1b', fontWeight: 700, fontSize: fs.bullet, flexShrink: 0, lineHeight: 1.7 }}>✕</Box>
              <Typography level="body-xs" sx={{ color: '#8b2020', fontSize: fs.body, lineHeight: 1.5 }}>{item}</Typography>
            </Box>
          ))}
          {avoidDynamic
            .filter(item => !avoidItems.some(ai => ai.includes(item.split(' ')[0])))
            .map((item, i) => (
            <Box key={`dyn${i}`} sx={{ display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
              <Box component="span" sx={{ fontSize: fs.bodyIcon, flexShrink: 0, lineHeight: 1.5 }}>⚠️</Box>
              <Typography level="body-xs" sx={{ color: '#7a4f00', fontSize: fs.body, lineHeight: 1.5 }}>{item}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bell note footer */}
      <Box sx={{ px: sp.hpx, py: 0.625, borderTop: '1px solid', borderColor: 'rgba(212,175,55,0.2)', background: 'rgba(212,175,55,0.04)', borderRadius: '0 0 8px 8px' }}>
        <Typography level="body-xs" sx={{ textAlign: 'center', color: 'text.tertiary', fontSize: fs.footer }}>
          Tap 🔔 above for detailed predictions &amp; alerts
        </Typography>
      </Box>

    </Box>
  );
}

// ---
// NoBirthDataPanel
// ---

function NoBirthDataPanel({ onGoToDashboard }: { onGoToDashboard: () => void }) {
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, px: 3, py: 4, textAlign: 'center' }}>
      <Box sx={{ width: 72, height: 72, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea22 0%, #764ba222 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px dashed #667eea55' }}>
        <AutoAwesomeIcon sx={{ fontSize: 32, color: '#667eea' }} />
      </Box>
      <Box>
        <Typography level="h4" sx={{ fontWeight: 700, mb: 0.75 }}>Add Your Birth Details</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary', lineHeight: 1.7, maxWidth: 280 }}>
          Your personalised chart and predictions will appear here once you submit your birth details in the chat.
        </Typography>
      </Box>
      <Divider sx={{ width: '80%' }} />
      <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {[
          { step: '1', label: 'Fill in your birth details in the chat' },
          { step: '2', label: 'Click Save & Calculate' },
          { step: '3', label: 'Your chart appears here instantly' },
          { step: '4', label: 'Ask Shreem anything about your life!' },
        ].map(({ step, label }) => (
          <Box key={step} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, borderRadius: 'md', bgcolor: 'background.level1' }}>
            <Box sx={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem' }}>{step}</Typography>
            </Box>
            <Typography level="body-sm" sx={{ fontWeight: 500 }}>{label}</Typography>
          </Box>
        ))}
      </Box>
      <Button onClick={onGoToDashboard} variant="outlined" color="neutral" size="sm" sx={{ mt: 1 }}>
        Go to Dashboard
      </Button>
    </Box>
  );
}

// ---
// CurrentPeriodPanel
// ---

const DASHA_COLORS: Record<string, string> = { MD: '#7c3aed', AD: '#d97706', PD: '#059669', SD: '#0284c7' };
const DASHA_LABELS: Record<string, string> = { MD: 'Mahadasha', AD: 'Antardasha', PD: 'Pratyantardasha', SD: 'Sookshma' };

// Nakshatra / sign lookup tables for detail cards
const _NAK_NAMES_P = ['Ashwini','Bharani','Krittika','Rohini','Mrigashira','Ardra','Punarvasu','Pushya','Ashlesha','Magha','Purva Phalguni','Uttara Phalguni','Hasta','Chitra','Swati','Vishakha','Anuradha','Jyeshtha','Mula','Purva Ashadha','Uttara Ashadha','Shravana','Dhanishtha','Shatabhisha','Purva Bhadrapada','Uttara Bhadrapada','Revati'];
const _SIGN_NAMES_P = ['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'];
const _HOUSE_LIFE_P: Record<number, string> = { 1:'Self', 2:'Wealth', 3:'Courage', 4:'Home', 5:'Children', 6:'Health', 7:'Spouse', 8:'Transformation', 9:'Fortune', 10:'Career', 11:'Gains', 12:'Spirituality' };
const _PLANET_CLR_P: Record<string, string> = { Sun:'#F59E0B', Moon:'#94A3B8', Mars:'#DC2626', Mercury:'#10B981', Jupiter:'#FBBF24', Venus:'#EC4899', Saturn:'#3B82F6', Rahu:'#8B5CF6', Ketu:'#F97316' };
const _DETAIL_LBL: Record<string, string> = { MD:'Maha Dasha — Your Life Theme', AD:'Antar Dasha — Medium-Term Theme', PD:'Pratyantar Dasha — Short-Term Theme', SD:'Sookshma Dasha — Immediate Theme' };

function _getLonInfo(lon: number | undefined): { nakshatra: string | null; sign: string | null } {
  if (lon === undefined) return { nakshatra: null, sign: null };
  const n = ((lon % 360) + 360) % 360;
  return { nakshatra: _NAK_NAMES_P[Math.floor(n / (360 / 27))] ?? null, sign: _SIGN_NAMES_P[Math.floor(n / 30)] ?? null };
}

function _durationStr(from: string, to: string): string {
  const ms = new Date(to).getTime() - new Date(from).getTime();
  if (!isFinite(ms) || ms <= 0) return '';
  const y = Math.floor(ms / (365.25 * 24 * 3600 * 1000));
  const m = Math.floor((ms % (365.25 * 24 * 3600 * 1000)) / (30.44 * 24 * 3600 * 1000));
  if (y === 0) return `${m}m`;
  if (m === 0) return `${y}y`;
  return `${y}y ${m}m`;
}

function CurrentPeriodPanel({ dasha, contextData }: { dasha: DashaContext; contextData: ChatSourceData }) {
  const fmt = (iso: string) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { month: 'short', year: 'numeric' }); } catch { return iso; }
  };
  const fmtFull = (iso: string) => {
    try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <TimelineIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
        <Typography level="title-md" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: '#7c3aed' }}>Current Dasha Periods</Typography>
      </Box>

      {/* ── Compact summary cards (MD / AD / PD / SD) ── */}
      {dasha.levels.map((lvl) => {
        const color = DASHA_COLORS[lvl.level] ?? '#555';
        const now = Date.now();
        const from = new Date(lvl.from).getTime();
        const to = new Date(lvl.to).getTime();
        const progress = Math.min(100, Math.max(0, ((now - from) / (to - from)) * 100));
        return (
          <Card key={lvl.level} variant="outlined" sx={{ p: 1.5, bgcolor: `${color}0d`, borderColor: `${color}44` }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Chip size="sm" sx={{ bgcolor: color, color: '#fff', fontWeight: 700, fontSize: '0.68rem' }}>{lvl.level}</Chip>
                  <Typography level="title-sm" sx={{ fontWeight: 700, color }}>{lvl.planet}</Typography>
                </Box>
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.25 }}>{DASHA_LABELS[lvl.level]}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography level="body-xs" sx={{ color: 'text.secondary', fontWeight: 600 }}>{fmt(lvl.from)}</Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>to {fmt(lvl.to)}</Typography>
              </Box>
            </Box>
            <LinearProgress determinate value={progress} sx={{ '--LinearProgress-radius': '4px', '--LinearProgress-progressThickness': '6px', '--LinearProgress-thickness': '6px', '& .MuiLinearProgress-progressBar': { bgcolor: color } }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {lvl.rules?.map(h => <Chip key={`r${h}`} size="sm" variant="soft" sx={{ fontSize: '0.62rem', color }}>H{h}</Chip>)}
                {lvl.occupies && <Chip size="sm" variant="outlined" sx={{ fontSize: '0.62rem', borderColor: color, color }}>In H{lvl.occupies}</Chip>}
              </Box>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{Math.round(progress)}% done</Typography>
            </Box>
          </Card>
        );
      })}

      {/* ── Separator ── */}
      <Divider sx={{ my: 0.25 }}>
        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem', px: 0.5 }}>Period Analysis</Typography>
      </Divider>

      {/* ── Detail cards for MD and AD ── */}
      {dasha.levels.filter(lvl => lvl.level === 'MD' || lvl.level === 'AD').map(lvl => {
        const grahas = contextData.astroData.grahas as Record<string, { lon: number; retrograde: boolean } | undefined>;
        const { nakshatra, sign } = _getLonInfo(grahas[lvl.planet]?.lon);
        const planetColor = _PLANET_CLR_P[lvl.planet] ?? DASHA_COLORS[lvl.level] ?? '#555';
        const borderColor = DASHA_COLORS[lvl.level] ?? '#555';
        const spotlight = HOUSE_KEYWORDS[lvl.occupies];
        const dur = _durationStr(lvl.from, lvl.to);
        const lifeAreas = [...new Set(
          [lvl.occupies, ...(lvl.rules ?? []), ...(lvl.aspects ?? [])]
            .map(h => _HOUSE_LIFE_P[h])
            .filter((x): x is string => Boolean(x))
        )];
        const ts = lvl.transitStats;
        const funcNature = (dasha.functional as Record<string, string> | undefined)?.[lvl.planet];

        return (
          <Card key={`detail_${lvl.level}`} variant="outlined" sx={{
            p: 1.75,
            borderLeft: '4px solid',
            borderLeftColor: borderColor,
            bgcolor: `${borderColor}06`,
          }}>
            {/* Title line */}
            <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.5, mb: 0.5 }}>
              {_DETAIL_LBL[lvl.level]}{dur ? ` (${dur})` : ''}{' — '}
              <Box component="span" sx={{ color: planetColor }}>{lvl.planet}</Box>
              {nakshatra && sign && (
                <> of <Box component="span" sx={{ color: '#b8860b' }}>{nakshatra}</Box> Nakshatra in <Box component="span" sx={{ color: '#1976d2' }}>{sign}</Box></>
              )}
              {grahas[lvl.planet]?.retrograde && (
                <Box component="span" sx={{ color: '#dc2626', fontSize: '0.72rem', ml: 0.5 }}>(R)</Box>
              )}
            </Typography>

            {/* Spotlight + functional nature */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75, flexWrap: 'wrap' }}>
              {spotlight && (
                <Chip size="sm" variant="solid" sx={{ bgcolor: `${borderColor}22`, color: borderColor, fontWeight: 600, fontSize: '0.65rem' }}>
                  House of {spotlight}
                </Chip>
              )}
              {funcNature && (
                <Chip size="sm" variant="soft"
                  color={funcNature === 'benefic' ? 'success' : funcNature === 'malefic' ? 'danger' : 'neutral'}
                  sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                  {funcNature === 'benefic' ? '✓ Benefic' : funcNature === 'malefic' ? '⚠ Malefic' : '○ Neutral'}
                </Chip>
              )}
              {(dasha.blendedPct as Record<string, number> | undefined)?.[lvl.planet] !== undefined && (
                <Chip size="sm" variant="outlined" sx={{ fontSize: '0.65rem' }}>
                  Strength {Math.round((dasha.blendedPct as Record<string, number>)[lvl.planet])}%
                </Chip>
              )}
            </Box>

            {/* Date range */}
            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 0.75 }}>
              {fmtFull(lvl.from)} — {fmtFull(lvl.to)}
            </Typography>

            {/* Occupies / rules / aspects */}
            <Typography level="body-xs" sx={{ mb: ts || lifeAreas.length > 0 ? 1 : 0, lineHeight: 1.65 }}>
              Occupies <strong>H{lvl.occupies}{spotlight ? ` (${spotlight})` : ''}</strong>
              {(lvl.rules ?? []).length > 0 && <>, rules <strong>H{lvl.rules.join(', H')}</strong></>}
              {(lvl.aspects ?? []).length > 0 && <>, aspects <strong>H{lvl.aspects!.join(', H')}</strong></>}
            </Typography>

            {/* Transit Analysis */}
            {ts && (
              <Box sx={{ mb: lifeAreas.length > 0 ? 1 : 0 }}>
                <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.4 }}>Transit Analysis:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="sm" variant="soft" color="success" sx={{ fontSize: '0.65rem' }}>
                    ✓ {Math.round(ts.beneficSupport)}% benefic support
                  </Chip>
                  <Chip size="sm" variant="soft" color="warning" sx={{ fontSize: '0.65rem' }}>
                    ⚠ {Math.round(ts.maleficPressure)}% malefic pressure
                  </Chip>
                  <Chip size="sm" variant="soft"
                    color={ts.avgTransits >= 5 ? 'danger' : ts.avgTransits >= 3 ? 'primary' : 'neutral'}
                    sx={{ fontSize: '0.65rem' }}>
                    {ts.avgTransits >= 5 ? '🔥' : ts.avgTransits >= 3 ? '⚡' : '○'} Transit support {ts.avgTransits.toFixed(1)}
                  </Chip>
                </Box>
              </Box>
            )}

            {/* Life Focus Areas */}
            {lifeAreas.length > 0 && (
              <Box>
                <Typography level="body-xs" sx={{ fontWeight: 600, mb: 0.4 }}>Life Focus Areas:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {lifeAreas.slice(0, 7).map(area => (
                    <Chip key={area} size="sm" variant="soft" color="primary" sx={{ fontSize: '0.65rem' }}>{area}</Chip>
                  ))}
                  {lifeAreas.length > 7 && (
                    <Chip size="sm" variant="outlined" color="neutral" sx={{ fontSize: '0.65rem' }}>+{lifeAreas.length - 7} more</Chip>
                  )}
                </Box>
              </Box>
            )}
          </Card>
        );
      })}

      <Typography level="body-xs" sx={{ fontFamily: '"Cormorant Garamond", "Garamond", serif', color: 'text.tertiary', textAlign: 'center', fontStyle: 'italic', mt: 0.5 }}>Ask the AI for deeper analysis of these periods →</Typography>
    </Box>
  );
}

// ---
// HouseScoreCard — shared mini-card used by HouseDashboardPanel + LifeAreaFocusPanel
// Shows combined score + expandable detail bars (bhava, planet, dasha, transit, friction)
// ---
function HouseScoreCard({
  house, combined, color, label,
  planetImpact, bpct, dImpact, tImpact, lfi,
}: {
  house: number; combined: number; color: string; label: string;
  planetImpact: number; bpct: number | null;
  dImpact: number | null; tImpact: number | null; lfi: number | null;
}) {
  const [expanded, setExpanded] = React.useState(false);

  // Bipolar bar (0-100, 50=neutral) → renders left or right of centre
  const BipolarBar = ({ value, clr }: { value: number; clr: string }) => {
    const delta = value - 50;
    const pct = Math.min(50, Math.abs(delta));
    return (
      <Box sx={{ position: 'relative', height: '4px', borderRadius: '3px', bgcolor: 'background.level2' }}>
        <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', bgcolor: 'divider' }} />
        {delta >= 0
          ? <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: `${pct}%`, bgcolor: clr, borderRadius: '0 3px 3px 0' }} />
          : <Box sx={{ position: 'absolute', right: '50%', top: 0, bottom: 0, width: `${pct}%`, bgcolor: '#dc2626', borderRadius: '3px 0 0 3px' }} />
        }
      </Box>
    );
  };

  return (
    <Box
      onClick={() => setExpanded(e => !e)}
      sx={{
        p: 1, borderRadius: 'md', bgcolor: 'background.level1',
        border: '1px solid', borderColor: expanded ? `${color}55` : 'divider',
        cursor: 'pointer', transition: 'border-color 0.15s',
        '&:hover': { borderColor: `${color}44` },
      }}
    >
      {/* Header row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Typography level="body-xs" sx={{ fontWeight: 700, color, fontSize: '0.72rem' }}>H{house} · {HOUSE_KEYWORDS[house]}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box component="span" sx={{ fontSize: '0.62rem', fontWeight: 700, px: 0.6, py: '1px', borderRadius: '6px', bgcolor: `${color}18`, color }}>{combined}%</Box>
          <Typography sx={{ fontSize: '0.6rem', color: 'text.tertiary' }}>{expanded ? '▲' : '▼'}</Typography>
        </Box>
      </Box>

      {/* Combined progress bar */}
      <LinearProgress determinate value={Math.min(100, Math.max(0, combined))}
        sx={{ '--LinearProgress-radius': '3px', '--LinearProgress-progressThickness': '4px', '--LinearProgress-thickness': '4px', '& .MuiLinearProgress-progressBar': { bgcolor: color } }} />

      {/* Summary label / expand hint */}
      {!expanded && (
        <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.3, fontSize: '0.6rem' }}>{label} · tap for details</Typography>
      )}

      {/* Expanded detail bars */}
      {expanded && (
        <Box sx={{ mt: 0.875, display: 'flex', flexDirection: 'column', gap: '5px' }}>

          {/* Bhava Bala */}
          {bpct !== null && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                <Typography sx={{ fontSize: '0.58rem', color: '#2563eb', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Bhava Bala</Typography>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#2563eb' }}>{Math.round(bpct)}%</Typography>
              </Box>
              <Box sx={{ height: '4px', borderRadius: '3px', bgcolor: 'background.level2', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${Math.min(100, bpct)}%`, bgcolor: '#2563eb', borderRadius: '3px' }} />
              </Box>
            </Box>
          )}

          {/* Planet Impact */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
              <Typography sx={{ fontSize: '0.58rem', color: '#d97706', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Planet Impact</Typography>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: planetImpact >= 0 ? '#059669' : '#dc2626' }}>
                {planetImpact >= 0 ? '+' : ''}{planetImpact.toFixed(1)}
              </Typography>
            </Box>
            <BipolarBar value={((planetImpact + 50) / 100) * 100} clr="#d97706" />
          </Box>

          {/* Dasha Impact */}
          {dImpact !== null && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#7c3aed', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Dasha</Typography>
                  <Box sx={{ fontSize: '0.48rem', px: 0.4, borderRadius: '3px', bgcolor: '#7c3aed22', color: '#7c3aed', fontWeight: 700, lineHeight: 1.4 }}>NOW</Box>
                </Box>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: dImpact >= 50 ? '#059669' : '#dc2626' }}>
                  {dImpact >= 50 ? '+' : ''}{Math.round(dImpact - 50)}
                </Typography>
              </Box>
              <BipolarBar value={dImpact} clr="#7c3aed" />
            </Box>
          )}

          {/* Transit Impact */}
          {tImpact !== null && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                  <Typography sx={{ fontSize: '0.58rem', color: '#0891b2', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Transit</Typography>
                  <Box sx={{ fontSize: '0.48rem', px: 0.4, borderRadius: '3px', bgcolor: '#0891b222', color: '#0891b2', fontWeight: 700, lineHeight: 1.4 }}>NOW</Box>
                </Box>
                <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: tImpact >= 50 ? '#059669' : '#dc2626' }}>
                  {tImpact >= 50 ? '+' : ''}{Math.round(tImpact - 50)}
                </Typography>
              </Box>
              <BipolarBar value={tImpact} clr="#0891b2" />
            </Box>
          )}

          {/* Life Friction Index */}
          {lfi !== null && (() => {
            const lvl = getFrictionLevel(lfi);
            const lfiColor = FRICTION_LEVEL_COLORS[lvl];
            return (
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '2px' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <Typography sx={{ fontSize: '0.58rem', color: lfiColor, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Life Friction</Typography>
                    <Box sx={{ fontSize: '0.48rem', px: 0.4, borderRadius: '3px', bgcolor: lfiColor + '22', color: lfiColor, fontWeight: 700, lineHeight: 1.4 }}>{lvl}</Box>
                  </Box>
                  <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: lfiColor }}>{lfi}%</Typography>
                </Box>
                <Box sx={{ height: '4px', borderRadius: '3px', bgcolor: 'background.level2', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${lfi}%`, bgcolor: lfiColor, borderRadius: '3px' }} />
                </Box>
              </Box>
            );
          })()}

        </Box>
      )}
    </Box>
  );
}

// ---
// HouseDashboardPanel
// ---

function HouseDashboardPanel({ contextData }: { contextData: ChatSourceData }) {
  const houseScores  = (contextData.d1HouseScores       ?? {}) as Record<number, number>;
  const bhavaBala    = (contextData.bhavaBalaRupas       ?? {}) as Record<number, number>;
  const dashaByH     = (contextData.dashaImpactByHouse   ?? {}) as Record<number, number>;
  const transitByH   = (contextData.transitImpactByHouse ?? {}) as Record<number, number>;
  const houseLFIs    = _buildHouseLFIs(contextData);

  const hasBhava   = Object.keys(bhavaBala).length > 0;
  const hasDasha   = Object.keys(dashaByH).length > 0;
  const hasTransit = Object.keys(transitByH).length > 0;
  const hasLFI     = houseLFIs !== null;

  const getColor = (s: number) => s >= 75 ? '#059669' : s >= 60 ? '#2563eb' : s >= 30 ? '#d97706' : '#dc2626';
  const getLabel = (s: number) => s >= 75 ? 'Excellent' : s >= 60 ? 'Good' : s >= 30 ? 'Fair' : 'Needs Care';

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <GridViewIcon sx={{ color: '#d97706', fontSize: 20 }} />
        <Typography level="title-md" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: '#d97706' }}>House Scores</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
        {Array.from({ length: 12 }, (_, i) => i + 1).map(h => {
          const planetImpact = houseScores[h] ?? 0;
          const bpct    = hasBhava   ? (bhavaBala[h]  ?? 50)  : null;
          const dImpact = hasDasha   ? (dashaByH[h]   ?? 50)  : null;
          const tImpact = hasTransit ? (transitByH[h] ?? 50)  : null;
          const lfi     = hasLFI     ? (houseLFIs![h] ?? null) : null;
          const combined = Math.round(computeHouseCombinedScore(bpct, planetImpact, dImpact ?? 50, tImpact ?? 50));
          const color = getColor(combined);
          return (
            <HouseScoreCard key={h} house={h} combined={combined} color={color} label={getLabel(combined)}
              planetImpact={planetImpact} bpct={bpct}
              dImpact={dImpact} tImpact={tImpact} lfi={lfi} />
          );
        })}
      </Box>
    </Box>
  );
}

// ---
// SwotSummaryPanel
// ---

function SwotSummaryPanel({ contextData }: { contextData: ChatSourceData }) {
  const dasha = contextData.dashaContext as DashaContext | undefined;
  const levels = dasha?.levels ?? [];
  const houseScores  = (contextData.d1HouseScores       ?? {}) as Record<number, number>;
  const bhavaBala    = (contextData.bhavaBalaRupas       ?? {}) as Record<number, number>;
  const dashaByH     = (contextData.dashaImpactByHouse   ?? {}) as Record<number, number>;
  const transitByH   = (contextData.transitImpactByHouse ?? {}) as Record<number, number>;

  const hasBhava   = Object.keys(bhavaBala).length > 0;
  const hasDasha   = Object.keys(dashaByH).length > 0;
  const hasTransit = Object.keys(transitByH).length > 0;

  // Rank by canonical combined score — identical to Dashboard
  const sortedHouses = Array.from({ length: 12 }, (_, i) => i + 1).map(h => ({
    house: h,
    combined: Math.round(computeHouseCombinedScore(
      hasBhava   ? (bhavaBala[h]  ?? 50)  : null,
      houseScores[h] ?? 0,
      hasDasha   ? (dashaByH[h]   ?? 50)  : 50,
      hasTransit ? (transitByH[h] ?? 50)  : 50,
    )),
  }));
  const topHouses  = [...sortedHouses].sort((a, b) => b.combined - a.combined).slice(0, 3);
  const weakHouses = [...sortedHouses].sort((a, b) => a.combined - b.combined).slice(0, 3);

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <AssessmentIcon sx={{ color: '#059669', fontSize: 20 }} />
        <Typography level="title-md" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: '#059669' }}>SWOT Overview</Typography>
      </Box>
      {levels.length > 0 && (
        <Card variant="outlined" sx={{ p: 1.5 }}>
          <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: 'text.secondary', mb: 1, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Active Dasha Periods</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {levels.map((l) => (
              <Chip key={l.level} size="sm" variant="soft" color={l.level === 'MD' ? 'primary' : l.level === 'AD' ? 'warning' : l.level === 'PD' ? 'success' : 'neutral'}>
                {l.level}: {l.planet}
              </Chip>
            ))}
          </Box>
        </Card>
      )}
      <Card variant="outlined" sx={{ p: 1.5, borderColor: 'success.outlinedBorder', bgcolor: 'success.softBg' }}>
        <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: 'success.700', mb: 1, textTransform: 'uppercase', letterSpacing: '0.08em' }}>💪 Strongest Houses</Typography>
        {topHouses.map(({ house, combined }) => (
          <Box key={house} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography level="body-sm">H{house} — {HOUSE_KEYWORDS[house] ?? ''}</Typography>
            <Chip size="sm" variant="soft" color="success">{combined}%</Chip>
          </Box>
        ))}
      </Card>
      <Card variant="outlined" sx={{ p: 1.5, borderColor: 'warning.outlinedBorder', bgcolor: 'warning.softBg' }}>
        <Typography level="body-xs" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: 'warning.700', mb: 1, textTransform: 'uppercase', letterSpacing: '0.08em' }}>⚠️ Areas Needing Attention</Typography>
        {weakHouses.map(({ house, combined }) => (
          <Box key={house} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography level="body-sm">H{house} — {HOUSE_KEYWORDS[house] ?? ''}</Typography>
            <Chip size="sm" variant="soft" color="warning">{combined}%</Chip>
          </Box>
        ))}
      </Card>
      <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center', fontStyle: 'italic' }}>Full SWOT analysis is loading in the chat \u2192</Typography>
    </Box>
  );
}

// ---
// LifeAreaFocusPanel
// ---

function LifeAreaFocusPanel({ contextData, lifeArea }: { contextData: ChatSourceData; lifeArea: LifeAreaKey }) {
  const areaConfig = LIFE_AREAS[lifeArea];
  const houses = areaConfig.houses;

  const asc = contextData.astroData.asc;
  const moon = contextData.astroData.moon;
  const lagnaLord = SIGN_LORD_MAP[asc.ascendantSignIndex] ?? 'Unknown';

  const basNakshatra = moon.nakshatra?.split(' - ')[0]?.split(' Pada')[0]?.trim() ?? '';
  const nakshatraTraits = NAKSHATRA_NATURE[basNakshatra] ?? '';
  const personality = ASCENDANT_PERSONALITY[asc.ascendantSign] ?? null;

  const houseScores  = (contextData.d1HouseScores       ?? {}) as Record<number, number>;
  const bhavaBala    = (contextData.bhavaBalaRupas       ?? {}) as Record<number, number>;
  const dashaByH     = (contextData.dashaImpactByHouse   ?? {}) as Record<number, number>;
  const transitByH   = (contextData.transitImpactByHouse ?? {}) as Record<number, number>;
  const houseLFIs    = _buildHouseLFIs(contextData);

  const hasDasha   = Object.keys(dashaByH).length > 0;
  const hasTransit = Object.keys(transitByH).length > 0;
  const hasBhava   = Object.keys(bhavaBala).length > 0;
  const hasLFI     = houseLFIs !== null;

  function _avg(map: Record<number, number>, fallback: number) {
    return houses.reduce((s, h) => s + (map[h] ?? fallback), 0) / Math.max(1, houses.length);
  }

  const planetImpactAvg  = _avg(houseScores, 0);
  const bhavaAvg         = hasBhava   ? _avg(bhavaBala, 50)  : null;
  const dashaImpactAvg   = hasDasha   ? _avg(dashaByH,  50)  : null;
  const transitImpactAvg = hasTransit ? _avg(transitByH, 50) : null;
  const lfiAvg           = hasLFI     ? Math.round(houses.reduce((s, h) => s + (houseLFIs![h] ?? 50), 0) / Math.max(1, houses.length)) : null;

  const combinedScore = Math.round(
    computeHouseCombinedScore(
      bhavaAvg,
      planetImpactAvg,
      dashaImpactAvg   ?? 50,
      transitImpactAvg ?? 50,
    )
  );

  const getScoreColor = (s: number) => s >= 75 ? '#059669' : s >= 60 ? '#2563eb' : s >= 30 ? '#d97706' : '#dc2626';
  const getScoreLabel = (s: number) => s >= 75 ? 'Excellent' : s >= 60 ? 'Good' : s >= 30 ? 'Fair' : 'Needs Attention';
  const areaColor = getScoreColor(combinedScore);

  const getHouseCardColor = (s: number) => s >= 75 ? '#059669' : s >= 60 ? '#2563eb' : s >= 30 ? '#d97706' : '#dc2626';
  const getHouseCardLabel = (s: number) => s >= 75 ? 'Excellent' : s >= 60 ? 'Good' : s >= 30 ? 'Fair' : 'Needs Care';

  // Bipolar bar (inline, for life area score card)
  const BipolarBar = ({ value, color }: { value: number; color: string }) => {
    const delta = value - 50;
    const pct = Math.min(50, Math.abs(delta));
    return (
      <Box sx={{ position: 'relative', height: '5px', borderRadius: '3px', bgcolor: 'background.level2' }}>
        <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: '1px', bgcolor: 'divider' }} />
        {delta >= 0
          ? <Box sx={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: `${pct}%`, bgcolor: color, borderRadius: '0 3px 3px 0' }} />
          : <Box sx={{ position: 'absolute', right: '50%', top: 0, bottom: 0, width: `${pct}%`, bgcolor: '#dc2626', borderRadius: '3px 0 0 3px' }} />
        }
      </Box>
    );
  };

  // Feeds relevant to this life area
  const relevantFeeds = (contextData.feeds ?? []).filter((f: any) => {
    const areaStr = (f.lifeArea ?? f.life_area ?? '').toLowerCase();
    return areaStr === lifeArea || areaStr.includes(lifeArea);
  }).slice(0, 4);

  // House activities: build per-house entry with lord-routing
  // (same logic as HouseBalanceSection: if H1 lord is in H10, show H10 activities to balance H1)
  const houseActivitiesMap = contextData.houseActivities ?? {};
  const placements = contextData.astroData?.placements ?? {};
  const ascSignIndex = contextData.astroData?.asc?.ascendantSignIndex ?? 1;

  // Sign (1-indexed) → ruling planet (same mapping as HouseBalanceSection)
  const SIGN_LORDS_PANEL: Record<number, string> = {
    1: 'Mars', 2: 'Venus', 3: 'Mercury', 4: 'Moon', 5: 'Sun', 6: 'Mercury',
    7: 'Venus', 8: 'Mars', 9: 'Jupiter', 10: 'Saturn', 11: 'Saturn', 12: 'Jupiter',
  };

  const houseActivityCards = houses.map(h => {
    // Only show activities for houses with negative planet impact (need balancing)
    if ((houseScores[h] ?? 0) >= 0) return null;
    // Sign of the house (whole-sign: house h has sign = ((ascSignIndex - 1 + h - 1) % 12) + 1)
    const houseSign = ((ascSignIndex - 1 + h - 1) % 12) + 1;
    const lord = SIGN_LORDS_PANEL[houseSign] ?? '';
    const lordHouse = (placements as any)[lord]?.house as number | undefined;
    const activitySourceHouse = lordHouse ?? h;
    const lordRouted = lordHouse !== undefined && lordHouse !== h;
    const activityText = houseActivitiesMap[activitySourceHouse];
    if (!activityText) return null;
    return { h, lord, lordHouse, activitySourceHouse, lordRouted, activityText };
  }).filter(Boolean) as { h: number; lord: string; lordHouse: number | undefined; activitySourceHouse: number; lordRouted: boolean; activityText: string }[];

  const STATUS_COLORS: Record<string, string> = {
    excellent: '#059669', good: '#2563eb', fair: '#d97706', needs_attention: '#dc2626',
  };

  function getActivityBorderColor(h: number): string {
    const bpct    = hasBhava   ? (bhavaBala[h]  ?? 50)  : null;
    const dImpact = hasDasha   ? (dashaByH[h]   ?? 50)  : null;
    const tImpact = hasTransit ? (transitByH[h] ?? 50)  : null;
    const score   = Math.round(computeHouseCombinedScore(bpct, houseScores[h] ?? 0, dImpact ?? 50, tImpact ?? 50));
    return score >= 75 ? STATUS_COLORS.excellent : score >= 60 ? STATUS_COLORS.good : score >= 30 ? STATUS_COLORS.fair : STATUS_COLORS.needs_attention;
  }

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Personality tile (Chart Essentials style) ── */}
      <Card variant="outlined" sx={{ p: 1.75, bgcolor: 'background.level1' }}>
        {/* Chart Essentials header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
          <AutoAwesomeIcon sx={{ color: '#d4af37', fontSize: 16 }} />
          <Typography level="title-sm" sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontWeight: 700, color: '#d4af37' }}>Chart Essentials</Typography>
        </Box>

        {/* 2-col info grid */}
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderRadius: 'sm', overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 1.25 }}>
          {[
            ['Ascendant (Lagna)', asc.ascendantSign],
            ['Moon Sign (Rashi)', moon.moonSign],
            ['Lagna Lord', lagnaLord],
            ['Moon Nakshatra', moon.nakshatra],
            ['Lagna Nakshatra', asc.ascendantNakshatra],
            ['', ''],
          ].map(([label, value], i) => (
            <Box key={i} sx={{
              px: 1.25, py: 0.75,
              borderRight: i % 2 === 0 ? '1px solid' : 'none',
              borderBottom: i < 4 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}>
              {label ? (
                <>
                  <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.6rem', mb: 0.15 }}>{label}</Typography>
                  <Typography level="body-xs" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{value}</Typography>
                </>
              ) : null}
            </Box>
          ))}
        </Box>

        {/* Ascendant personality block */}
        {personality && (
          <Box sx={{ mb: 1.25, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontFamily: '"Cinzel", "Garamond", serif', fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.07em', color: 'text.tertiary', textTransform: 'uppercase', mb: 0.5 }}>
              {asc.ascendantSign} Ascendant — Personality
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.75rem', fontWeight: 600, mb: 0.25 }}>{personality.title}</Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.73rem' }}>{personality.desc}</Typography>
          </Box>
        )}

        {/* Moon nakshatra block */}
        {nakshatraTraits && (
          <Box sx={{ pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.07em', color: '#b45309', textTransform: 'uppercase', mb: 0.4 }}>
              {basNakshatra} Moon — Nature & Temperament
            </Typography>
            <Typography level="body-xs" sx={{ color: 'text.secondary', lineHeight: 1.6, fontSize: '0.73rem' }}>{nakshatraTraits}</Typography>
          </Box>
        )}
      </Card>

      {/* ── Life Area Score Card ── */}
      <Card variant="outlined" sx={{ p: 1.75, borderLeft: '4px solid', borderLeftColor: areaColor, bgcolor: `${areaColor}06` }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{ fontSize: '1rem' }}>{areaConfig.icon}</Typography>
            <Typography level="title-sm" sx={{ fontWeight: 700 }}>{areaConfig.label}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography level="body-xs" sx={{ fontWeight: 800, fontSize: '1rem', color: areaColor }}>{combinedScore}%</Typography>
            <Chip size="sm" sx={{ bgcolor: `${areaColor}22`, color: areaColor, fontWeight: 700, fontSize: '0.65rem' }}>{getScoreLabel(combinedScore)}</Chip>
          </Box>
        </Box>
        <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 1.25, fontSize: '0.71rem' }}>{areaConfig.description}</Typography>

        {/* Metric bars */}
        {bhavaAvg !== null && (
          <Box sx={{ mb: 0.875 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography level="body-xs" sx={{ color: '#2563eb', fontWeight: 600, fontSize: '0.65rem' }}>● BHAVA BALA</Typography>
              <Typography level="body-xs" sx={{ fontSize: '0.65rem', color: 'text.secondary', fontWeight: 600 }}>{bhavaAvg.toFixed(1)}</Typography>
            </Box>
            <LinearProgress determinate value={Math.min(100, bhavaAvg)} sx={{ '--LinearProgress-radius': '3px', '--LinearProgress-progressThickness': '5px', '--LinearProgress-thickness': '5px', '& .MuiLinearProgress-progressBar': { bgcolor: '#2563eb' } }} />
          </Box>
        )}

        {/* Planet Impact — bipolar -50..+50 */}
        <Box sx={{ mb: 0.875 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography level="body-xs" sx={{ color: '#d97706', fontWeight: 600, fontSize: '0.65rem' }}>● PLANET IMPACT</Typography>
            <Typography level="body-xs" sx={{ fontSize: '0.65rem', color: planetImpactAvg >= 0 ? '#059669' : '#dc2626', fontWeight: 600 }}>
              {planetImpactAvg >= 0 ? '+' : ''}{planetImpactAvg.toFixed(1)}
            </Typography>
          </Box>
          {/* Scale -50..+50 into bipolar 0..100 before passing to BipolarBar */}
          <BipolarBar value={((planetImpactAvg + 50) / 100) * 100} color="#d97706" />
        </Box>

        {/* Dasha Impact */}
        {dashaImpactAvg !== null && (
          <Box sx={{ mb: transitImpactAvg !== null ? 0.875 : 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography level="body-xs" sx={{ color: '#7c3aed', fontWeight: 600, fontSize: '0.65rem' }}>
                ● DASHA IMPACT{' '}
                <Box component="span" sx={{ display: 'inline-block', fontSize: '0.56rem', px: 0.5, borderRadius: '4px', bgcolor: '#7c3aed22', color: '#7c3aed', fontWeight: 700, verticalAlign: 'middle', ml: 0.25 }}>NOW</Box>
              </Typography>
              <Typography level="body-xs" sx={{ fontSize: '0.65rem', color: dashaImpactAvg >= 50 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                {dashaImpactAvg >= 50 ? '+' : ''}{Math.round(dashaImpactAvg - 50)}
              </Typography>
            </Box>
            <BipolarBar value={dashaImpactAvg} color="#7c3aed" />
          </Box>
        )}

        {/* Transit Impact */}
        {transitImpactAvg !== null && (
          <Box sx={{ mb: lfiAvg !== null ? 0.875 : 0.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
              <Typography level="body-xs" sx={{ color: '#0891b2', fontWeight: 600, fontSize: '0.65rem' }}>
                ● TRANSIT IMPACT{' '}
                <Box component="span" sx={{ display: 'inline-block', fontSize: '0.56rem', px: 0.5, borderRadius: '4px', bgcolor: '#0891b222', color: '#0891b2', fontWeight: 700, verticalAlign: 'middle', ml: 0.25 }}>NOW</Box>
              </Typography>
              <Typography level="body-xs" sx={{ fontSize: '0.65rem', color: transitImpactAvg >= 50 ? '#059669' : '#dc2626', fontWeight: 600 }}>
                {transitImpactAvg >= 50 ? '+' : ''}{Math.round(transitImpactAvg - 50)}
              </Typography>
            </Box>
            <BipolarBar value={transitImpactAvg} color="#0891b2" />
          </Box>
        )}

        {/* Life Friction Index */}
        {lfiAvg !== null && (() => {
          const lvl = getFrictionLevel(lfiAvg);
          const lfiColor = FRICTION_LEVEL_COLORS[lvl];
          return (
            <Box sx={{ mb: 0.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                <Typography level="body-xs" sx={{ color: lfiColor, fontWeight: 600, fontSize: '0.65rem' }}>
                  ● LIFE FRICTION{' '}
                  <Box component="span" sx={{ display: 'inline-block', fontSize: '0.56rem', px: 0.5, borderRadius: '4px', bgcolor: lfiColor + '22', color: lfiColor, fontWeight: 700, verticalAlign: 'middle', ml: 0.25 }}>{lvl}</Box>
                </Typography>
                <Typography level="body-xs" sx={{ fontSize: '0.65rem', fontWeight: 700, color: lfiColor }}>{lfiAvg}%</Typography>
              </Box>
              <Box sx={{ height: '5px', borderRadius: '3px', bgcolor: 'background.level2', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', width: `${lfiAvg}%`, bgcolor: lfiColor, borderRadius: '3px' }} />
              </Box>
            </Box>
          );
        })()}
      </Card>

      {/* ── Relevant House Cards ── */}
      <Box>
        <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.75 }}>
          Key Houses for {areaConfig.label}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
          {houses.map(h => {
            const planetImpact = houseScores[h] ?? 0;
            const bpct    = hasBhava   ? (bhavaBala[h]  ?? 50)  : null;
            const dImpact = hasDasha   ? (dashaByH[h]   ?? 50)  : null;
            const tImpact = hasTransit ? (transitByH[h] ?? 50)  : null;
            const lfi     = hasLFI     ? (houseLFIs![h] ?? null) : null;
            const hCombined = Math.round(computeHouseCombinedScore(bpct, planetImpact, dImpact ?? 50, tImpact ?? 50));
            return (
              <HouseScoreCard key={h} house={h} combined={hCombined} color={getHouseCardColor(hCombined)} label={getHouseCardLabel(hCombined)}
                planetImpact={planetImpact} bpct={bpct}
                dImpact={dImpact} tImpact={tImpact} lfi={lfi} />
            );
          })}
        </Box>
      </Box>

      {/* ── House Activities ── */}
      {houseActivityCards.length > 0 && (
        <Box>
          <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.75 }}>
            🔧 Activities to Strengthen Weak Houses
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.875 }}>
            {houseActivityCards.map(({ h, lord, activitySourceHouse, lordRouted, activityText }) => {
              const borderColor = getActivityBorderColor(h);
              return (
                <Box key={h} sx={{
                  p: 1.25, borderRadius: 'md',
                  borderLeft: `3px solid ${borderColor}`,
                  bgcolor: `${borderColor}06`,
                  border: `1px solid ${borderColor}25`,
                }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.375 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: borderColor }}>
                      H{h} · {HOUSE_KEYWORDS[h]}
                    </Typography>
                    {lordRouted && (
                      <Typography sx={{ fontSize: '0.6rem', color: 'text.tertiary', fontStyle: 'italic' }}>
                        via H{activitySourceHouse} ({lord} lord)
                      </Typography>
                    )}
                  </Box>
                  {/* Activity text */}
                  <Typography level="body-xs" sx={{ fontSize: '0.72rem', lineHeight: 1.65, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                    {activityText}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* ── Relevant Feeds ── */}
      {relevantFeeds.length > 0 && (
        <Box>
          <Typography level="body-xs" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.07em', mb: 0.75 }}>
            Active Signals
          </Typography>
          {relevantFeeds.map((f: any, idx: number) => (
            <Box key={idx} sx={{ mb: 0.75, p: 1, borderRadius: 'sm', bgcolor: 'background.level1', border: '1px solid', borderColor: 'divider' }}>
              <Typography level="body-xs" sx={{ fontSize: '0.72rem', lineHeight: 1.5 }}>{f.message ?? f.title ?? ''}</Typography>
              {f.shortMessage && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', fontSize: '0.65rem', mt: 0.25 }}>{f.shortMessage}</Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center', fontStyle: 'italic', mt: 0.5 }}>
        Ask Shreem about your {areaConfig.label.toLowerCase()} prospects →
      </Typography>
    </Box>
  );
}

// ---
// Main Component
// ---

// Ordered sequence for Next navigation
const LIFE_AREA_NAV_ORDER: LifeAreaKey[] = [
  'self', 'health', 'wealth', 'job', 'business',
  'marriage', 'children', 'education', 'property', 'mother', 'father',
];

export default function ChatOutputPanel({
  outputType,
  contextData,
  onGoToDashboard,
  onOutputTypeChange,
  onLifeAreaChange,
  userName,
  selectedLifeArea,
}: ChatOutputPanelProps) {

  const isDetail = outputType !== 'welcome';
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Scroll back to top whenever the user switches life area
  React.useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedLifeArea]);

  // Enrich contextData with fallback dasha/transit maps if the pre-computed
  // versions are missing (e.g. useMemo evaluated before functionalNatureData was ready)
  const enriched = contextData ? enrichContextData(contextData) : null;

  const wrap = (children: React.ReactNode) => (
    <Box sx={{
      width: '100%', height: '100%',
      borderLeft: '1px solid', borderColor: 'divider',
      bgcolor: 'background.level1',
      display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      opacity: 0.82,
      transition: 'opacity 0.2s',
      '&:hover': { opacity: 1 },
    }}>
      {/* Back navigation (all non-welcome views) */}
      {isDetail && (
        <Box sx={{
          display: 'flex', alignItems: 'center',
          px: 1.5, py: 0.625,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'background.level1',
          flexShrink: 0,
        }}>
          {/* ← Back to Insights (left side) */}
          <Box
            onClick={() => onOutputTypeChange?.('welcome')}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.625,
              cursor: 'pointer', flex: 1,
              '&:hover .back-label': { color: 'text.primary' },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography className="back-label" level="body-xs" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.72rem', transition: 'color 0.15s' }}>
              Back to Insights
            </Typography>
          </Box>
          {/* Prev / Next life area — only on the life_area view */}
          {outputType === 'life_area' && selectedLifeArea && onLifeAreaChange && (() => {
            const curIdx = LIFE_AREA_NAV_ORDER.indexOf(selectedLifeArea);
            if (curIdx === -1) return null;
            const prevArea = LIFE_AREA_NAV_ORDER[(curIdx - 1 + LIFE_AREA_NAV_ORDER.length) % LIFE_AREA_NAV_ORDER.length];
            const nextArea = LIFE_AREA_NAV_ORDER[(curIdx + 1) % LIFE_AREA_NAV_ORDER.length];
            const prevLabel = LIFE_AREAS[prevArea]?.label ?? prevArea;
            const nextLabel = LIFE_AREAS[nextArea]?.label ?? nextArea;
            return (
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title={prevLabel} size="sm" placement="bottom">
                  <Box
                    component="button"
                    onClick={() => { onLifeAreaChange(prevArea); onOutputTypeChange?.('life_area'); }}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: '50%',
                      border: '1px solid rgba(184,48,80,0.4)',
                      bgcolor: 'rgba(184,48,80,0.07)', cursor: 'pointer', outline: 'none', p: 0,
                      color: '#b83050',
                      '&:hover': { bgcolor: 'rgba(184,48,80,0.16)', borderColor: 'rgba(184,48,80,0.7)' },
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    <ArrowBackIosIcon sx={{ fontSize: '0.6rem', ml: '2px' }} />
                  </Box>
                </Tooltip>
                <Tooltip title={nextLabel} size="sm" placement="bottom">
                  <Box
                    component="button"
                    onClick={() => { onLifeAreaChange(nextArea); onOutputTypeChange?.('life_area'); }}
                    sx={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 22, height: 22, borderRadius: '50%',
                      border: '1px solid rgba(184,48,80,0.4)',
                      bgcolor: 'rgba(184,48,80,0.07)', cursor: 'pointer', outline: 'none', p: 0,
                      color: '#b83050',
                      '&:hover': { bgcolor: 'rgba(184,48,80,0.16)', borderColor: 'rgba(184,48,80,0.7)' },
                      transition: 'background 0.15s, border-color 0.15s',
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: '0.6rem' }} />
                  </Box>
                </Tooltip>
              </Box>
            );
          })()}
        </Box>
      )}
      {/* Scrollable content area */}
      <Box ref={scrollContainerRef} sx={{
        flex: 1, overflowY: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(184,48,80,0.3) transparent',
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: 'rgba(184,48,80,0.3)', borderRadius: 4 },
        '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(184,48,80,0.55)', borderRadius: 4 }
      }}>
        {children}
      </Box>
    </Box>
  );

  if (!contextData) return wrap(<NoBirthDataPanel onGoToDashboard={onGoToDashboard} />);

  // Normalise dashaContext — it can arrive as DashaContext {levels[]} or as the
  // flat {MD, AD, PD, SD, window} format used by dashaContextForFeeds in App.tsx.
  const dasha: DashaContext | undefined = (() => {
    const raw = contextData.dashaContext;
    if (!raw) return undefined;
    if ('levels' in raw && Array.isArray((raw as DashaContext).levels)) {
      return raw as DashaContext;
    }
    // Flat format → convert to DashaContext
    const flat = raw as { MD?: any; AD?: any; PD?: any; SD?: any; window?: any };
    const levels = (['MD', 'AD', 'PD', 'SD'] as const)
      .filter(k => flat[k])
      .map(k => ({ level: k, ...flat[k] }));
    if (levels.length === 0) return undefined;
    return { levels, window: flat.window } as unknown as DashaContext;
  })();

  switch (outputType) {
    case 'current_period':
      return wrap(dasha
        ? <CurrentPeriodPanel dasha={dasha} contextData={enriched ?? contextData!} />
        : <Box sx={{ p: 3 }}><Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Dasha data not available.</Typography></Box>
      );
    case 'house_dashboard':
      return wrap(contextData?.d1HouseScores && enriched
        ? <HouseDashboardPanel contextData={enriched} />
        : <Box sx={{ p: 3 }}><Typography level="body-sm" sx={{ color: 'text.tertiary' }}>House data not available.</Typography></Box>
      );
    case 'swot':
      return wrap(<SwotSummaryPanel contextData={enriched ?? contextData!} />);
    case 'life_remedies': {
      if (!contextData.houseLords) {
        return wrap(<Box sx={{ p: 3 }}><Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Remedy data not available yet. Please wait a moment and try again.</Typography></Box>);
      }
      // Map LifeAreaKey → LifeAreaRemediesTable name (its LIFE_AREAS constant)
      const AREA_NAME_MAP: Partial<Record<LifeAreaKey, string>> = {
        health: 'Health', wealth: 'Wealth', job: 'Job',
        business: 'Business', marriage: 'Marriage', children: 'Children',
      };
      const simplePlacements: Record<string, number> = {};
      Object.entries(contextData.astroData.placements).forEach(([planet, p]) => {
        simplePlacements[planet] = (p as any).house;
      });
      const getPlanetColor = (p: string): string => {
        const fn = (contextData.functionalNature as Record<string, string> | undefined)?.[p];
        if (fn === 'benefic' || fn === 'yoga_karaka') return '#16a34a';
        if (fn === 'malefic') return '#dc2626';
        return '#d4b106';
      };
      return wrap(
        <Box sx={{ p: 1.5 }}>
          <LifeAreaRemediesTable
            houseScores={contextData.d1HouseScores ?? {}}
            placements={simplePlacements}
            houseLords={contextData.houseLords as any}
            houseAspects={contextData.houseAspects}
            planetStrengths={contextData.blendedPlanetPct as any}
            getPlanetColor={getPlanetColor}
            filterLifeArea={selectedLifeArea ? AREA_NAME_MAP[selectedLifeArea] : undefined}
          />
        </Box>
      );
    }
    case 'life_area':
      if (selectedLifeArea && PERSONAL_AREA_META[selectedLifeArea] && enriched) {
        return wrap(
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <PersonalLifeAreaPanel contextData={enriched} lifeArea={selectedLifeArea} onLifeAreaChange={onLifeAreaChange} onOutputTypeChange={onOutputTypeChange} />
            <LifeAreaFocusPanel contextData={enriched} lifeArea={selectedLifeArea} />
          </Box>
        );
      }
      return wrap(selectedLifeArea && enriched
        ? <LifeAreaFocusPanel contextData={enriched} lifeArea={selectedLifeArea} />
        : <Box sx={{ p: 3 }}><Typography level="body-sm" sx={{ color: 'text.tertiary' }}>Select a life area from the sidebar.</Typography></Box>
      );
    default:
      return wrap(
        <InsightsWelcomePanel
          contextData={enriched ?? contextData!}
          userName={userName}
          onOutputTypeChange={onOutputTypeChange}
          onLifeAreaChange={onLifeAreaChange}
          selectedLifeArea={selectedLifeArea}
        />
      );
  }
}
// ===== END FILE: src/components/chat/ChatOutputPanel.tsx =====
